import React, { useState, useEffect, useMemo, useRef, useContext } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  TextField,
  IconButton,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import axios from "axios";
import FloatingLoader from "../../../FloatingLoader";
import { useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import CustomDropdown from "../../../CustomDropdownWithPlus";
import CustomFileUploader from "../../../CustomFileUploader";
import DropArea from "../../../../pages/instances/production/dashboard/purchasing-popup/drag-and-drop/DropArea";
import {
  BinatedLifecycleStatusPanel,
  BinatedLifecycleTimeline,
  BinatedLifecycleFilesTable,
  BinatedLifecycleRevisionModal,
} from "../../../BinatedLifecycle";
import "./index.css";
import { getCustomerFilesWithMeta, uploadFile } from "../../../../services/customerFiles";
import { AuthContext } from '../../../../helpers/AuthContext';
import {
  createProofMutationGuard,
  canManagePoProofs,
  canMutatePurchaseOrder,
  getRoleList,
  PROOF_MANAGEMENT_READ_ONLY_MESSAGE,
} from '../PurchaseOrderTable/poReadOnly';
import {
  buildProofReadinessPatch,
} from "./proofReadinessStatus";
import {
  buildPurchaseOrderProofStatusPatch,
  getPurchaseOrderProofStatusLabel,
  getPurchaseOrderProofStatusForReadiness,
  normalizePurchaseOrderProofStatus,
  PURCHASE_ORDER_PROOF_STATUS_OPTIONS,
} from "../../../../constants/purchaseOrderProofStatuses";

// API
import {
  fetchAllProofsList,
  approveProofList,
  uploadProofList,
  fetchAllDesignReps,
  updatePurchaseOrderDetails, // ← PO-level updates (same flow PurchasingPopup uses)
  lookupPurchaseOrders, // ← authoritative source for PO flags
  initiateBinatedProof,
  refreshBinatedProofStatus,
  requestBinatedProofRevision,
  updateBinatedProofOrder,
  invalidatePurchaseOrderCache,
  publishPurchaseOrderInvalidation,
} from "../../../../services/purchaseOrder";

// —————————————————————————————————————————————————————
// Helpers
// —————————————————————————————————————————————————————

const j = (obj) => {
  try {
    return JSON.stringify(
      obj,
      (k, v) => (typeof v === "bigint" ? String(v) : v),
      2
    );
  } catch (e) {
    return `<<JSON stringify error: ${e?.message}>>`;
  }
};

// "Optimistic" PO flags from proofs — instant, no extra fetch.
// We only consider rows that actually have a real uploaded file.
const derivePoFlagsFromProofs = (list = []) => {
  const byPo = {};
  const rowsByPo = {};

  for (const p of list) {
    if (!p?.id || String(p.id).toLowerCase() === "undefined") continue;
    const pid = String(p.poId);
    const url = String(p.proofFilePath || p.filePath || "");
    const hasFile = url && !/placeholder|sample|dummy|temp|draft/i.test(url);
    if (!hasFile) continue;

    if (!rowsByPo[pid]) rowsByPo[pid] = [];
    rowsByPo[pid].push(p);
  }

  Object.entries(rowsByPo).forEach(([pid, rows]) => {
    byPo[pid] = rows.length > 0 && rows.every((r) => !!r.allProofsUploaded);
  });

  return byPo;
};

// FAST gate computation: for each PO, true when (has ≥1 real file) AND (every real-file row has allProofsUploaded)
const computeRowsGateByPo = (list = []) => {
  const map = {}; // { [poId]: { hasFile: bool, allReady: bool } }
  for (const p of list) {
    if (!p?.id || String(p.id).toLowerCase() === "undefined") continue;
    const pid = String(p.poId);
    const hasFile = hasUploadedFile(p);
    if (!map[pid]) map[pid] = { hasFile: false, allReady: true };
    if (hasFile) {
      map[pid].hasFile = true;
      if (!p.allProofsUploaded) map[pid].allReady = false;
    }
  }
  const out = {};
  for (const [pid, v] of Object.entries(map)) out[pid] = v.hasFile && v.allReady;
  return out; // { [poId]: boolean }
};

const log = (...args) => console.log("[ProofsList]", ...args);

const getClientProofUrl = (poId) => {
  const base =
    process.env.REACT_APP_FRONTEND_URL ||
    process.env.REACT_APP_PUBLIC_URL ||
    window.location.origin;
  return `${base}/generated-proofs/${poId}`;
};

const getClientSingleProofUrl = (poId, proofId) => {
  const base =
    process.env.REACT_APP_FRONTEND_URL ||
    process.env.REACT_APP_PUBLIC_URL ||
    window.location.origin;
  return `${base}/generated-proof/${poId}/${proofId}`;
};

const getGeneratedPurchaseOrderUrl = (poId) => {
  if (!poId) return "";
  const base =
    process.env.REACT_APP_FRONTEND_URL ||
    process.env.REACT_APP_PUBLIC_URL ||
    window.location.origin;
  return `${base}/generated-purchase-order/${poId}`;
};

const extractGeneratedPurchaseOrderLinks = (value = "") => {
  const text = String(value || "");
  const matches = text.match(/https?:\/\/[^\s]+\/generated-purchase-order\/\d+/gi) || [];
  return [...new Set(matches.map((m) => m.trim()))];
};

const extractGeneratedPurchaseOrderIds = (value = "") => {
  const text = String(value || "");
  const fromUrls = [...text.matchAll(/generated-purchase-order\/(\d+)/gi)].map((m) => String(m?.[1] || "").trim());
  const fromNumbers = [...text.matchAll(/(?:^|[,\s])(\d{1,})(?=$|[,\s])/g)].map((m) => String(m?.[1] || "").trim());
  return [...new Set([...fromUrls, ...fromNumbers].filter(Boolean))];
};

const normalizeGeneratedPurchaseOrderReference = (value = "") => {
  const ids = extractGeneratedPurchaseOrderIds(value);
  if (!ids.length) return String(value || "").trim();
  const links = ids.map((id) => getGeneratedPurchaseOrderUrl(id)).filter(Boolean);
  return [...new Set(links)].join(", ");
};

// Color maps
const APPROVAL_COLOR = {
  approved: "success",
  rejected: "error",
  pending: "warning",
};

const STATUS_COLOR = {
  Requested: "info",
  Uploaded: "primary",
  Approved: "success",
  Rejected: "error",
};

const PROOF_FILE_STATUS_OPTIONS = [
  { id: "Proof Needed", name: "Proof Needed" },
  { id: "Uploaded", name: "Proof Uploaded" },
  { id: "Requested", name: getPurchaseOrderProofStatusLabel("Requested") },
  { id: "Approved", name: "Approved" },
  { id: "Rejected", name: "Rejected" },
];

const getProofFileStatusLabel = (status) =>
  status === "Requested" ? getPurchaseOrderProofStatusLabel(status) : status;

const approvalTooltip = (who, a = {}) => {
  const s = a?.state || "pending";
  const by = a?.by ? ` by ${a.by}` : "";
  const at = a?.at ? ` on ${new Date(a.at).toLocaleString()}` : "";
  return `${who}: ${s}${by}${at}`;
};

const oneLineWrap = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "nowrap",
  overflow: "hidden",
};

const chipTight = {
  height: 24,
  "& .MuiChip-label": { px: 1, fontSize: 12 },
};

const extFromUrl = (url) => {
  try {
    const u = new URL(url);
    const path = u.pathname.toLowerCase();
    const m = path.match(/\.([a-z0-9]+)$/i);
    return m ? m[1] : "";
  } catch {
    const m = String(url).toLowerCase().match(/\.([a-z0-9]+)(?:\?|#|$)/i);
    return m ? m[1] : "";
  }
};

const isImageExt = (ext) =>
  ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext);

const isPdfExt = (ext) => ext === "pdf";

// Flexible truthy coercion
const truthy = (v) => v === true || v === "true" || v === 1 || v === "1";

// Only treat real uploaded file URLs as “has file”
const hasUploadedFile = (p) => {
  const url = String(p?.proofFilePath || p?.filePath || "");
  if (!url) return false;
  return !/placeholder|sample|dummy|temp|draft/i.test(url);
};

// Normalize rows from API
const normalizeRow = (r) => ({
  ...r,
  proofStatus: r.proofStatus || r.status || "Proof Needed",
  proofFilePath: r.proofFilePath || r.filePath || null,
  poBinatedMeta: r.poBinatedMeta || r.binatedMeta || r.po?.binatedMeta || null,
  allProofsUploaded: truthy(
    r.allProofsUploaded ?? r.all_files_uploaded ?? r.rowReady ?? r.ready ?? false
  ),
});

const toAuditTrail = (proof) => {
  const raw = proof?.auditTrail;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

// Build per-PO aggregates (quick peek). Approvals ignore rows without real file.
const summarizeRows = (rows = []) => {
  const counts = {
    Uploaded: 0,
    Requested: 0,
    Approved: 0,
    Rejected: 0,
    client: { approved: 0, rejected: 0, pending: 0 },
    user: { approved: 0, rejected: 0, pending: 0 },
  };

  rows.forEach((row) => {
    const st = row.proofStatus || "Proof Needed";
    if (
      st === "Uploaded" ||
      st === "Requested" ||
      st === "Approved" ||
      st === "Rejected"
    ) {
      counts[st] += 1;
    }

    if (hasUploadedFile(row)) {
      const c = (row.approvals?.client?.state || "pending").toLowerCase();
      const u = (row.approvals?.user?.state || "pending").toLowerCase();
      if (["approved", "rejected", "pending"].includes(c)) counts.client[c] += 1;
      else counts.client.pending += 1;
      if (["approved", "rejected", "pending"].includes(u)) counts.user[u] += 1;
      else counts.user.pending += 1;
    }
  });

  return counts;
};

const aggToColor = (agg) => {
  if (agg.rejected > 0) return "error";
  if (agg.pending > 0) return "warning";
  if (agg.approved > 0) return "success";
  return "default";
};

// Compact status chips for main row
const buildStatusChips = (counts) => {
  const chips = [];
  ["Uploaded", "Approved", "Requested", "Rejected"].forEach((k) => {
    const n = counts[k];
    if (n > 0) {
      chips.push({
        key: k,
        label: `${getProofFileStatusLabel(k)} ${n}`,
        color: STATUS_COLOR[k] || "default",
        variant: k === "Requested" ? "outlined" : "filled",
      });
    }
  });
  return chips;
};

// —————————————————————————————————————————————————————
// Component
// —————————————————————————————————————————————————————

const ProofsList = () => {
  // tabId == salesOrderId
  const { instanceUniqueId, tabId, orderId } = useParams();
  const { authState } = useContext(AuthContext);
  const currentUser = authState?.user || authState || {};
  const canManageProofs = canManagePoProofs(currentUser);
  const canOverrideProofApproval = canMutatePurchaseOrder(currentUser, { action: 'override-proof-approval' });
  const canAccessProofManager = canManageProofs || canOverrideProofApproval;
  const blockProofMutation = createProofMutationGuard(
    currentUser,
    (message) => toast.error(message, { id: 'po-proof-management-read-only' })
  );
  const didFastGateRef = useRef({}); // prevent re-firing per PO

  const [proofs, setProofs] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [designerSelections, setDesignerSelections] = useState({});
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // Mirror of authoritative PO flags (from DB via lookupPurchaseOrders)
  const [poFlagsById, setPoFlagsById] = useState({}); // { [poId]: boolean }
  const [poProofStatusById, setPoProofStatusById] = useState({});
  const [poFlagsLoading, setPoFlagsLoading] = useState(false);
  const [poFlagUpdatingById, setPoFlagUpdatingById] = useState({});
  const [poProofStatusUpdatingById, setPoProofStatusUpdatingById] = useState({});
  const hasPoFlag = (map, pid) =>
    Object.prototype.hasOwnProperty.call(map, String(pid));

  // Upload & manage
  const [openModal, setOpenModal] = useState(false);
  const [selectedProofId, setSelectedProofId] = useState(null);
  const [managePoId, setManagePoId] = useState(null);
  // Reuse the existing PO role normalization; avoid hiding the recovery
  // action for managers whose authorization comes from the instance context.
  const roles = getRoleList(currentUser);
  const canAttachApprovedPo = canManageProofs || canOverrideProofApproval ||
    roles.some((role) => ["administrator", "admin", "super-administrator",
      "order-rep", "client-rep", "purchasing-rep", "production-rep"].includes(role));
  const [attachPoId, setAttachPoId] = useState(null);
  const [attachCandidates, setAttachCandidates] = useState([]);
  const [attachDiagnostics, setAttachDiagnostics] = useState(null);
  const [attachLoading, setAttachLoading] = useState(false);
  const [attachSaving, setAttachSaving] = useState(false);
  const openAttachApproved = async (poId) => {
    setAttachPoId(poId);
    setAttachCandidates([]);
    setAttachDiagnostics(null);
    setAttachLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/purchase-order/${poId}/approved-proof-candidates`,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      setAttachCandidates(data?.candidates || []);
      setAttachDiagnostics(data?.diagnostics || null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to find approved sales-order proofs.");
    } finally {
      setAttachLoading(false);
    }
  };
  const attachApproved = async (mappingId) => {
    if (!attachPoId || attachSaving) return;
    setAttachSaving(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/purchase-order/${attachPoId}/attach-approved-proof`,
        { mappingId },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      toast.success("Approved proof attached. You can attach another.");
      // Keep this dialog open so a package with several proofs can be mapped
      // without restarting the workflow after each attachment.
      const [candidateResponse] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/purchase-order/${attachPoId}/approved-proof-candidates`,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        ),
        fetchProofs(),
        loadPoFlags(),
      ]);
      setAttachCandidates(candidateResponse.data?.candidates || []);
      setAttachDiagnostics(candidateResponse.data?.diagnostics || null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to attach approved proof.");
    } finally {
      setAttachSaving(false);
    }
  };

  const detachApproved = async (mappingId) => {
    if (!attachPoId || attachSaving) return;
    if (!window.confirm("Remove this inherited PO proof? The original customer approval will remain saved.")) return;
    setAttachSaving(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/purchase-order/${attachPoId}/detach-approved-proof`,
        { mappingId },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      const [candidateResponse] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/purchase-order/${attachPoId}/approved-proof-candidates`,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        ),
        fetchProofs(),
        loadPoFlags(),
      ]);
      setAttachCandidates(candidateResponse.data?.candidates || []);
      setAttachDiagnostics(candidateResponse.data?.diagnostics || null);
      toast.success("Inherited proof removed. Select the correct approved proof.");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to remove inherited proof.");
    } finally {
      setAttachSaving(false);
    }
  };
  const [uploadPoId, setUploadPoId] = useState(null);
  const [manageSimpleMode, setManageSimpleMode] = useState(true);
  const [manageUploadingAll, setManageUploadingAll] = useState(false);
  const [approvedProofsOpen, setApprovedProofsOpen] = useState(false);
  const [approvedProofsLoading, setApprovedProofsLoading] = useState(false);
  const [approvedProofs, setApprovedProofs] = useState([]);
  const [reuseTarget, setReuseTarget] = useState(null);
  const [reuseRequiresApproval, setReuseRequiresApproval] = useState(true);
  const [customerId, setCustomerId] = useState(null);

  // Delete row confirm
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [manageTab, setManageTab] = useState("proof_management");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyProof, setHistoryProof] = useState(null);
  const [overrideConfirm, setOverrideConfirm] = useState(null);
  const [overrideConfirmBusy, setOverrideConfirmBusy] = useState(false);
  const [manageBinatedTab, setManageBinatedTab] = useState("status");
  const [binatedFormByPo, setBinatedFormByPo] = useState({});
  const [selectedProofIndexByPo, setSelectedProofIndexByPo] = useState({});
  const [selectedProofImportKeyByPo, setSelectedProofImportKeyByPo] = useState({});
  const [binatedLifecycleRefreshing, setBinatedLifecycleRefreshing] = useState(false);
  const [binatedRevisionModalOpen, setBinatedRevisionModalOpen] = useState(false);
  const [binatedRevisionTargetFile, setBinatedRevisionTargetFile] = useState(null);
  const [binatedRevisionNote, setBinatedRevisionNote] = useState("");
  const [binatedRevisionFiles, setBinatedRevisionFiles] = useState([]);
  const [binatedRevisionSubmitting, setBinatedRevisionSubmitting] = useState(false);
  const [binatedSubmitting, setBinatedSubmitting] = useState(false);
  const [binatedSettings, setBinatedSettings] = useState({
    enabled: false,
    ready: false,
    provider: "binated",
  });
  const effectiveManageSimpleMode = canManageProofs ? manageSimpleMode : true;

  // Reset fast-gate tracker when switching SO tabs
  useEffect(() => {
    didFastGateRef.current = {};
  }, [tabId]);

  useEffect(() => {
    const fetchCustomerId = async () => {
      if (!orderId) return;
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/orders/get-order/${orderId}`
        );
        const attached = data?.order?.attachedCustomers || [];
        const first = attached[0];
        const id = typeof first === "object" ? first?.id : first;
        setCustomerId(id || null);
      } catch (err) {
        console.warn("Failed to resolve customerId for proofs", err?.message);
        setCustomerId(null);
      }
    };
    fetchCustomerId();
  }, [orderId]);

  const loadBinatedSettings = async () => {
    if (!instanceUniqueId) return;
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${instanceUniqueId}/proof-provider-settings`,
        { headers: { accessToken: token } }
      );
      const proofProvider = res?.data?.proofProvider || {};
      const cfg = proofProvider?.binated || {};
      setBinatedSettings({
        enabled: proofProvider?.enabled === true,
        provider: proofProvider?.provider || "binated",
        ready:
          proofProvider?.enabled === true &&
          (proofProvider?.provider || "binated") === "binated" &&
          cfg?.hasApiKey === true &&
          cfg?.hasApiSecret === true &&
          !!cfg?.baseUrl,
      });
    } catch (err) {
      console.warn("Proof provider settings unavailable for this user/session", err?.response?.status, err?.message);
      setBinatedSettings({ enabled: false, ready: false, provider: "binated" });
    }
  };

  // Fetch
  const fetchProofs = async () => {
    try {
      setLoading(true);
      const response = await fetchAllProofsList(tabId);

      console.groupCollapsed("[ProofsList] fetchAllProofsList raw");
      log("tabId:", tabId);
      console.log(j(response));
      console.groupEnd();

      const list = (response?.proofs || []).map(normalizeRow);

      console.groupCollapsed("[ProofsList] normalized proofs (first 5)");
      console.log(j(list.slice(0, 5)));
      log("count:", list.length);
      console.groupEnd();

      setProofs(list);

      // Instant seed so the checkbox can render checked right away
      const optimistic = derivePoFlagsFromProofs(list);
      setPoFlagsById((prev) => {
        const next = { ...prev };
        for (const [pid, val] of Object.entries(optimistic)) {
          if (!(pid in next)) next[pid] = val; // don’t clobber if already known
        }
        return next;
      });

      // seed designer dropdowns
      const init = {};
      list.forEach((p) => {
        if (p?.designerUserId != null) init[p.id] = String(p.designerUserId);
      });
      setDesignerSelections(init);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load proofs");
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedProofs = async () => {
    if (!customerId) return;
    setApprovedProofsLoading(true);
    try {
      const files = await getCustomerFilesWithMeta({
        customerId,
        tag: "proof-request",
        status: "approved",
        folder: "Approved Proofs",
      });
      setApprovedProofs(files || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load approved proofs");
    } finally {
      setApprovedProofsLoading(false);
    }
  };

  // Authoritative PO flags loader (PurchasingPopup parity)
  const loadPoFlags = async () => {
    try {
      setPoFlagsLoading(true);
      const res = await lookupPurchaseOrders({
        salesOrder: tabId,
      });

      console.groupCollapsed("[ProofsList] lookupPurchaseOrders raw");
      console.log(j(res));
      console.groupEnd();

      const rows = res?.rows || res?.purchaseOrders || res || [];
      const map = {};
      const proofStatusMap = {};
      rows.forEach((po) => {
        const id = String(po.id ?? po.poId ?? "");
        if (!id) return;
        const flag =
          po.allProofsUploaded ??
          po?.flags?.allProofsUploaded ??
          po?.AllProofsUploaded;
        map[id] = !!flag;
        proofStatusMap[id] = normalizePurchaseOrderProofStatus(po.proofStatus);
      });

      console.groupCollapsed("[ProofsList] computed poFlagsById");
      console.log(j(map));
      console.groupEnd();

      setPoFlagsById(map);
      setPoProofStatusById(proofStatusMap);
    } catch (e) {
      console.warn("[ProofsList] loadPoFlags failed:", e?.message || e);
    } finally {
      setPoFlagsLoading(false);
    }
  };

  const fetchDesigners = async () => {
    try {
      const res = await fetchAllDesignReps();
      if (res?.designers) {
        setDesigners(
          res.designers.map((u) => ({
            id: String(u.id),
            name: u.name || u.email || `User ${u.id}`,
          }))
        );
      }
    } catch (e) {
      console.error(e);
      // optional list — non-fatal
    }
  };

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      setInitializing(true);
      await Promise.allSettled([
        fetchProofs(),
        loadPoFlags(), // authoritative PO flag snapshot
        fetchDesigners(),
        loadBinatedSettings(),
      ]);
      if (mounted) setInitializing(false);
    };

    init();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId, instanceUniqueId]);

  const updatePoBinatedMeta = (poId, meta = {}) => {
    setProofs((prev) =>
      prev.map((row) =>
        String(row.poId) === String(poId)
          ? {
              ...row,
              poBinatedMeta: { ...(row.poBinatedMeta || {}), ...meta },
              binatedMeta: { ...(row.binatedMeta || {}), ...meta },
            }
          : row
      )
    );
  };

  const getPoBinatedMeta = (po = {}) => po?.binatedMeta || {};
  const hasBinatedStarted = (po = {}) => {
    const meta = getPoBinatedMeta(po);
    return Boolean(meta?.binatedOrderId || meta?.orderId || meta?.proofRequestedAt);
  };
  const isBinatedUpdateAllowed = (po = {}) => {
    const meta = getPoBinatedMeta(po);
    const status = String(meta?.binatedStatus || meta?.binatedProofStatus || "").trim().toLowerCase();
    return [
      "quoted",
      "quote approved",
      "quote modification",
      "ordered",
      "assigned to production",
      "production in progress",
      "production completed",
      "sampling in progress",
      "sampling completed",
      "ds in progress",
      "ds completed",
      "qc in progress",
      "qc completed",
      "on hold",
    ].includes(status);
  };

  const handleBinatedStart = async (poId, payload = null) => {
    if (blockProofMutation({ action: 'manage-binated-proofs' })) return;
    try {
      const res = await initiateBinatedProof(poId, payload);
      updatePoBinatedMeta(poId, res?.binated || {});
      toast.success("Sent to Binated.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to start Binated proof.");
    }
  };

  const handleBinatedRefresh = async (poId) => {
    try {
      setBinatedLifecycleRefreshing(true);
      const res = await refreshBinatedProofStatus(poId);
      updatePoBinatedMeta(poId, res?.binated || {});
      toast.success("Binated status refreshed.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to refresh Binated status.");
    } finally {
      setBinatedLifecycleRefreshing(false);
    }
  };

  const buildBinatedRevisionPayload = ({ poId, note = "", fileRefs = [] } = {}) => {
    const po = proofsByPo[String(poId)] || {};
    const meta = getPoBinatedMeta(po);
    const form = getProofingFormState(poId);
    const previousPayload = meta?.proofSyncPayload && typeof meta.proofSyncPayload === "object" ? meta.proofSyncPayload : {};
    const previousMedia = Array.isArray(previousPayload?.media) ? previousPayload.media[0] || {} : {};
    const previousReference = String(
      previousMedia?.reference || meta?.reference || form?.reference || `PO-${po?.poNumber || poId}`
    ).trim();

    const fallbackUrl =
      fileRefs[0] ||
      String(form?.proofUrl || "").trim() ||
      String(previousMedia?.url || "").trim() ||
      "";

    const instructionsParts = [
      String(note || "").trim(),
      fileRefs.length ? `Revision files: ${fileRefs.join(", ")}` : "",
    ].filter(Boolean);

    return {
      media: [
        {
          ...previousMedia,
          description: String(previousMedia?.description || form?.proofDescription || `PO ${po?.poNumber || ""}`).trim(),
          reference: previousReference,
          instructions: instructionsParts.join("\n"),
          url: fallbackUrl,
          is_adjustment: "true",
          previous_order_reference: previousReference,
        },
      ],
      revisionRequest: true,
      revisionMeta: {
        note: String(note || "").trim(),
        fileRefs,
      },
    };
  };

  const handleBinatedRevision = async (poId, note = "", fileRefs = []) => {
    if (blockProofMutation({ action: 'manage-binated-proofs' })) return;
    try {
      const payload = buildBinatedRevisionPayload({ poId, note, fileRefs });
      const res = await requestBinatedProofRevision(poId, payload);
      updatePoBinatedMeta(poId, res?.binated || {});
      toast.success("Binated revision requested.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to request Binated revision.");
    }
  };

  const handleBinatedUpdate = async (poId, payload) => {
    if (blockProofMutation({ action: 'manage-binated-proofs' })) return;
    try {
      const res = await updateBinatedProofOrder(poId, payload);
      updatePoBinatedMeta(poId, res?.binated || {});
      toast.success("Binated order updated.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update Binated order.");
    }
  };

  const openProofHistory = (proof) => {
    setHistoryProof(proof || null);
    setHistoryOpen(true);
  };

  const buildStartProofPayload = (poId) => {
    const po = proofsByPo[String(poId)] || {};
    const rows = rowsForPo(String(poId));
    const firstRow = rows[0] || po?.decorations?.[0] || {};
    const emailValue = "";
    const contactName = "Approver";

    const uniq = (arr = []) => Array.from(new Set(arr.filter(Boolean)));
    const cleanText = (val) => {
      const s = String(val ?? "").trim();
      if (!s) return "";
      if (["undefined", "null", "n/a", "na"].includes(s.toLowerCase())) return "";
      return s;
    };
    const firstNonEmpty = (...vals) =>
      vals.find((v) => {
        if (Array.isArray(v)) return v.length > 0;
        return cleanText(v) !== "";
      });
    const getField = (row = {}, keys = []) => {
      for (const key of keys) {
        if (row?.[key] != null && cleanText(row[key]) !== "") return row[key];
      }
      const lc = {};
      Object.keys(row || {}).forEach((k) => {
        lc[k.toLowerCase()] = row[k];
      });
      for (const key of keys) {
        const val = lc[String(key).toLowerCase()];
        if (val != null && cleanText(val) !== "") return val;
      }
      const nested = [row?.details, row?.decorationDetails, row?.DecoratorDetails].filter(
        (v) => v && typeof v === "object"
      );
      for (const bag of nested) {
        for (const key of keys) {
          if (bag?.[key] != null && cleanText(bag[key]) !== "") return bag[key];
        }
      }
      return undefined;
    };
    const parseArtworkDetailsText = (raw = "") => {
      if (raw && typeof raw === "object") {
        return {
          "Imprint Type": raw?.imprintType || raw?.ImprintType || raw?.methodName || "",
          "Logo Size": raw?.logoSize || raw?.size || "",
          "Color of Logo": raw?.logoColor || raw?.colorOfLogo || "",
          "Logo Name": raw?.logoName || raw?.artworkName || "",
          "Logo Location": raw?.location || raw?.placementName || "",
        };
      }
      const lines = String(raw)
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      const skip = /^(?:\d+\s+)?artwork details$/i;
      const map = {};
      for (let i = 0; i < lines.length; i += 1) {
        const key = lines[i];
        const next = lines[i + 1];
        if (!next || skip.test(key)) continue;
        if (!map[key]) map[key] = next;
      }
      return map;
    };
    const parseMaybeJsonLocal = (val, fallback = {}) => {
      if (val == null) return fallback;
      if (typeof val === "object") return val;
      try {
        return JSON.parse(val);
      } catch {
        return fallback;
      }
    };
    const parseLogoSize = (raw = "") => {
      const str = String(raw).toLowerCase();
      const match =
        str.match(/([0-9]+(?:\\.[0-9]+)?)\\s*(?:\"|inches|inch|in|cm|mm)?\\s*[xX]\\s*([0-9]+(?:\\.[0-9]+)?)/) ||
        str.match(
          /([0-9]+(?:\\.[0-9]+)?)\\s*(?:\"|inches|inch|in|cm|mm)?\\s*(?:by)\\s*([0-9]+(?:\\.[0-9]+)?)/i
        );
      if (!match) return { width: 100, height: 100 };
      return { width: Number(match[1]), height: Number(match[2]) };
    };
    const inferProcessType = (row = {}) => {
      const parsed = parseArtworkDetailsText(
        row?.artworkDetails || row?.artworkDetail || row?.details || row?.decorationDetails || ""
      );
      const supplierInfo = parseMaybeJsonLocal(row?.supplierInfo, {});
      const chargeText = Array.isArray(supplierInfo?.charges)
        ? supplierInfo.charges.map((c) => c?.Name).filter(Boolean).join(" ")
        : "";
      const text = String(
        firstNonEmpty(
          row?.imprintType,
          getField(row, ["imprint_type", "Imprint Type", "imprint type"]),
          row?.decoration,
          row?.decorationType,
          row?.process_type,
          parsed["Imprint Type"],
          chargeText
        ) || ""
      ).toLowerCase();
      if (text.includes("embroider")) return "EMB";
      if (text.includes("screen")) return "SCREEN";
      if (text.includes("digit")) return "DIGITIZING";
      if (text.includes("dtf")) return "DTF";
      if (text.includes("dtg")) return "DTG";
      if (text.includes("laser")) return "LASER";
      return "EMB";
    };
    const sourceRows = rows.length
      ? rows
      : Array.isArray(po?.decorations)
        ? po.decorations
        : [firstRow];
    const buildPlacementForRow = (row = {}) => {
      const parsed = parseArtworkDetailsText(
        row?.artworkDetails || row?.artworkDetail || row?.details || row?.decorationDetails || ""
      );
      const rowUrls = uniq([
        row?.proofFilePath,
        row?.filePath,
        row?.Image,
        row?.url,
        row?.thumbnailUrl,
        row?.imageUrl,
        ...(Array.isArray(row?.artworks)
          ? row.artworks.map((a) => a?.filePath || a?.Image || a?.url)
          : []),
      ]);
      const dim = parseLogoSize(
        firstNonEmpty(
          row?.logoSize,
          row?.size,
          getField(row, ["logo_size", "Logo Size", "logo size"]),
          parsed["Logo Size"]
        ) || ""
      );
      const placementName = String(
        firstNonEmpty(
          row?.placementName,
          row?.location,
          row?.Location,
          getField(row, ["logoLocation", "logo_location", "Logo Location", "logo location"]),
          parsed["Logo Location"]
        ) || ""
      );
      const logoColors = firstNonEmpty(
        row?.colors,
        row?.logoColor,
        row?.colorOfLogo,
        getField(row, ["colorOfLogo", "logo_color", "Color of Logo", "logo color"]),
        parsed["Color of Logo"]
      );
      const proofText = firstNonEmpty(
        row?.text,
        row?.proofComments,
        getField(row, ["logoName", "logo_name", "Logo Name", "Artwork Name"]),
        parsed["Logo Name"],
        parsed["Artwork Name"]
      );
      return {
        // Keep placement fields blank on initial load; users can provide these explicitly.
        placement_code: "",
        placement_name: "",
        process_type: inferProcessType(row),
        reference_decoration_id: "",
        height: Number.isFinite(dim.height) ? dim.height : 100,
        width: Number.isFinite(dim.width) ? dim.width : 100,
        measurement_unit: "in",
        product_color: String(row?.productColor || row?.color || ""),
        colors: Array.isArray(logoColors)
          ? logoColors
          : String(logoColors || "")
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean),
        background_color: String(row?.backgroundColor || ""),
        text: Array.isArray(proofText)
          ? proofText
          : String(proofText || "")
              .split("\\n")
              .map((t) => t.trim())
              .filter(Boolean),
        url: rowUrls,
        sewout_logo_recreation: false,
        output_file_format: "png",
        is_proofing: true,
        email_users: emailValue
          ? [{ name: contactName, email: emailValue, permission: "approver" }]
          : [],
      };
    };
    const placements = sourceRows.map(buildPlacementForRow);
    const fallbackUrls = uniq([
      firstRow?.proofFilePath,
      firstRow?.filePath,
      firstRow?.Image,
      firstRow?.url,
      ...(Array.isArray(firstRow?.artworks)
        ? firstRow.artworks.map((a) => a?.filePath || a?.Image || a?.url)
        : []),
    ]);
      const firstParsed = parseArtworkDetailsText(
      firstRow?.artworkDetails || firstRow?.artworkDetail || firstRow?.details || firstRow?.decorationDetails || ""
    );
    const safePlacements = placements.length
      ? placements
      : [
          {
            placement_code: "",
            placement_name: "",
            process_type: "EMB",
            reference_decoration_id: "",
            height: 100,
            width: 100,
            measurement_unit: "px",
            product_color: "",
            colors: [],
            background_color: "",
            text: [],
            url: fallbackUrls,
            sewout_logo_recreation: false,
            output_file_format: "png",
            is_proofing: true,
            email_users: [],
          },
        ];
    const decorationInfo = [
      {
        decoration: String(
          firstNonEmpty(
            firstRow?.imprintType,
            getField(firstRow, ["imprint_type", "Imprint Type", "imprint type"]),
            firstParsed["Imprint Type"],
            "Digitizing"
          )
        ),
        decoration_instructions: cleanText(
          firstNonEmpty(
            firstRow?.instructions,
            getField(firstRow, ["instructions", "proofInstructions", "Proof Instructions"]),
            firstParsed["Proof Required"],
            ""
          )
        ),
        placement_info: safePlacements,
      },
    ];
    return {
      media: [
        {
          product_sku: po?.poNumber ? `PO-${po.poNumber}` : "",
          priority: "Next Day",
          due_hours: 24,
          description: "",
          reference: String(orderId || ""),
          instructions: "",
          product_category: "Apparel",
          product: "",
          product_url: "",
          garment: "",
          change_request: false,
          decoration_info: decorationInfo,
        },
      ],
    };
  };

  const buildUpdateProofPayload = (poId) => {
    const po = proofsByPo[String(poId)] || {};
    const meta = getPoBinatedMeta(po);
    const rows = rowsForPo(String(poId));
    const firstRow = rows[0] || {};
    return {
      media: [
        {
          description: meta?.description || `PO ${po?.poNumber || ""}`.trim(),
          reference: String(orderId || ""),
          process_type: firstRow?.processType || "",
          instructions: "",
          url: firstRow?.proofFilePath || firstRow?.filePath || "",
          colors: "",
          height: "100",
          width: "100",
          placement: firstRow?.placementName || "",
          product_sku: po?.poNumber ? `PO-${po.poNumber}` : "",
          sewout_logo_recreation: "No",
          cap: "No",
          background_color: "",
          garment: "",
          product: po?.purchaseOrderType || "",
          is_adjustment: "false",
          previous_order_reference: meta?.binatedOrderId || "",
          output_file_format: "png",
        },
      ],
    };
  };

  const buildProofEntriesForPo = (po = {}, rows = []) => {
    const normalizedRows = Array.isArray(rows) ? rows : [];
    const withFile = normalizedRows.filter((r) => hasUploadedFile(r));
    const source = withFile.length ? withFile : normalizedRows;
    const entries = source
      .map((row = {}) => {
        const url = String(row?.proofFilePath || row?.filePath || "").trim();
        if (!url) return null;
        const artworkHints = [
          row?.artworkDetails,
          row?.details,
          row?.proofComments,
          row?.logoName ? `Logo: ${row.logoName}` : "",
          row?.colorOfLogo ? `Colors: ${row.colorOfLogo}` : "",
          row?.logoSize ? `Logo Size: ${row.logoSize}` : "",
          row?.placementName ? `Placement: ${row.placementName}` : "",
        ]
          .map((s) => String(s || "").trim())
          .filter(Boolean);
        return {
          url,
          proof_info: {
            product: String(row?.productName || row?.itemName || po?.purchaseOrderType || "").trim(),
            garment: String(row?.garment || "").trim(),
            height: String(row?.height || "100").trim(),
            width: String(row?.width || "100").trim(),
            description: artworkHints.join("\n"),
          },
        };
      })
      .filter(Boolean);
    return entries.length
      ? entries
      : [
          {
            url: "",
            proof_info: {
              product: "",
              garment: "",
              height: "100",
              width: "100",
              description: "",
            },
          },
        ];
  };

  const normalizeProofEntries = (proofs = []) => {
    if (!Array.isArray(proofs) || proofs.length === 0) return [];
    return proofs.map((proof = {}) => ({
      url: String(proof?.url || "").trim(),
      proof_info: {
        product: String(proof?.proof_info?.product || "").trim(),
        garment: String(proof?.proof_info?.garment || "").trim(),
        height: String(proof?.proof_info?.height || "").trim(),
        width: String(proof?.proof_info?.width || "").trim(),
        description: String(proof?.proof_info?.description || "").trim(),
      },
    }));
  };

  const buildProofingFormState = (poId) => {
    return {
      reference: "",
      notes: "",
      cobranding: true,
      smartSend: true,
      company_logo_url: "",
      proofs: [
        {
          url: "",
          proof_info: {
            product: "",
            garment: "",
            height: "100",
            width: "100",
            description: "",
          },
        },
      ],
      approverName: "",
      approverEmail: "",
      approverPermission: "approver",
      reminderFrequency: "week",
      reminderInterval: 1,
      reminderWeekdays: "Mon",
      reminderTimezone: "America/Los_Angeles",
      reminderTime: "09:00",
      reminderEndDate: "",
    };
  };
  const getProofingFormState = (poId) => {
    const key = String(poId);
    return binatedFormByPo[key] || buildProofingFormState(poId);
  };
  const updateProofingFormState = (poId, patch = {}) => {
    const key = String(poId);
    setBinatedFormByPo((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || buildProofingFormState(poId)), ...patch },
    }));
  };
  const getSelectedProofIndex = (poId, proofs = []) => {
    const key = String(poId);
    const idx = Number(selectedProofIndexByPo[key] || 0);
    if (!Array.isArray(proofs) || proofs.length === 0) return 0;
    return Math.max(0, Math.min(idx, proofs.length - 1));
  };
  const setSelectedProofIndex = (poId, idx) => {
    const key = String(poId);
    setSelectedProofIndexByPo((prev) => ({ ...prev, [key]: Math.max(0, Number(idx) || 0) }));
  };
  const updateProofEntry = (poId, proofIndex, patch = {}) => {
    const form = getProofingFormState(poId);
    const proofs = normalizeProofEntries(form?.proofs);
    const idx = Math.max(0, Math.min(Number(proofIndex) || 0, Math.max(proofs.length - 1, 0)));
    const current = proofs[idx] || {
      url: "",
      proof_info: { product: "", garment: "", height: "100", width: "100", description: "" },
    };
    proofs[idx] = {
      ...current,
      ...patch,
      proof_info: {
        ...(current.proof_info || {}),
        ...(patch.proof_info || {}),
      },
    };
    updateProofingFormState(poId, { proofs });
  };
  const addProofEntry = (poId) => {
    const form = getProofingFormState(poId);
    const proofs = normalizeProofEntries(form?.proofs);
    proofs.push({
      url: "",
      proof_info: { product: "", garment: "", height: "100", width: "100", description: "" },
    });
    updateProofingFormState(poId, { proofs });
    setSelectedProofIndex(poId, proofs.length - 1);
  };
  const removeProofEntry = (poId) => {
    const form = getProofingFormState(poId);
    const proofs = normalizeProofEntries(form?.proofs);
    if (proofs.length <= 1) return;
    const idx = getSelectedProofIndex(poId, proofs);
    proofs.splice(idx, 1);
    updateProofingFormState(poId, { proofs });
    setSelectedProofIndex(poId, Math.max(0, idx - 1));
  };
  const getProofImportOptions = (poId) => {
    const rows = rowsForPo(String(poId));
    const withFile = rows.filter((row) => hasUploadedFile(row));
    const source = withFile.length ? withFile : rows;
    return source.map((row, idx) => {
      const rowId = row?.id != null ? String(row.id) : `row-${idx}`;
      const location = String(row?.placementName || row?.location || "").trim();
      const product = String(row?.productName || row?.itemName || "").trim();
      return {
        key: `${rowId}-${idx}`,
        row,
        label: [product || `Proof Row ${idx + 1}`, location ? `(${location})` : ""].filter(Boolean).join(" "),
      };
    });
  };
  const importProofRowToCurrent = (poId, optionKey) => {
    const options = getProofImportOptions(poId);
    const selected = options.find((opt) => opt.key === optionKey);
    if (!selected) return;
    const entry = buildProofEntriesForPo(proofsByPo[String(poId)] || {}, [selected.row])[0];
    if (!entry) return;
    const form = getProofingFormState(poId);
    const proofs = normalizeProofEntries(form?.proofs);
    const idx = getSelectedProofIndex(poId, proofs);
    proofs[idx] = entry;
    updateProofingFormState(poId, { proofs });
  };
  const importAllProofRows = (poId) => {
    const po = proofsByPo[String(poId)] || {};
    const rows = rowsForPo(String(poId));
    const entries = buildProofEntriesForPo(po, rows);
    updateProofingFormState(poId, { proofs: entries });
    setSelectedProofIndex(poId, 0);
  };
  const buildPoFeedPayloadExample = (poId) => {
    const po = proofsByPo[String(poId)] || {};
    const rows = rowsForPo(String(poId));
    const mappedProofs = buildProofEntriesForPo(po, rows);
    return {
      purchase_order: {
        id: po?.id || poId,
        poNumber: po?.poNumber || "",
        purchaseOrderType: po?.purchaseOrderType || "",
        supplierName: po?.supplierName || "",
      },
      products_from_po_feed: rows.map((row) => ({
        rowId: row?.id || null,
        product: row?.productName || row?.itemName || "",
        garment: row?.garment || "",
        placement: row?.placementName || row?.location || "",
        artworkDetails: row?.artworkDetails || row?.details || "",
        proofUrl: row?.proofFilePath || row?.filePath || "",
      })),
      mapped_proofs_example: mappedProofs,
    };
  };
  const toProofingPayload = (form = {}) => {
    const normalizedProofs = normalizeProofEntries(form?.proofs);
    const fallbackProof = {
      url: String(form.proofUrl || "").trim(),
      proof_info: {
        product: String(form.proofProduct || "").trim(),
        garment: String(form.proofGarment || "").trim(),
        height: String(form.proofHeight || "").trim(),
        width: String(form.proofWidth || "").trim(),
        description: String(form.proofDescription || "").trim(),
      },
    };
    return {
      reference: String(form.reference || "").trim(),
      notes: String(form.notes || "").trim(),
      cobranding: !!form.cobranding,
      company_logo_url: String(form.company_logo_url || "").trim(),
      proofs: (normalizedProofs.length ? normalizedProofs : [fallbackProof]).filter(
        (proof) => String(proof?.url || "").trim() !== ""
      ),
      email_users: [
        {
          name: String(form.approverName || "").trim(),
          email: String(form.approverEmail || "").trim(),
          permission: String(form.approverPermission || "approver").trim(),
          reminder_schedule: {
            frequency: String(form.reminderFrequency || "week").trim(),
            interval: Number(form.reminderInterval || 1),
            weekdays: String(form.reminderWeekdays || "")
              .split(",")
              .map((d) => d.trim())
              .filter(Boolean),
            timezone: String(form.reminderTimezone || "").trim(),
            time: String(form.reminderTime || "").trim(),
            end_date: String(form.reminderEndDate || "").trim(),
          },
        },
      ],
    };
  };
  const openBinatedRevisionModal = (file = null) => {
    setBinatedRevisionTargetFile(file);
    setBinatedRevisionNote("");
    setBinatedRevisionFiles([]);
    setBinatedRevisionModalOpen(true);
  };
  const closeBinatedRevisionModal = () => {
    if (binatedRevisionSubmitting) return;
    setBinatedRevisionModalOpen(false);
  };
  const submitBinatedRevisionModal = async () => {
    if (!managePoId) return;
    const note = String(binatedRevisionNote || "").trim();
    if (!note) {
      toast.error("Revision note is required.");
      return;
    }
    try {
      setBinatedRevisionSubmitting(true);
      const fileRefs = [];
      if (binatedRevisionTargetFile?.downloadUrl) {
        fileRefs.push(binatedRevisionTargetFile.downloadUrl);
      }
      if (customerId && Array.isArray(binatedRevisionFiles) && binatedRevisionFiles.length > 0) {
        for (const file of binatedRevisionFiles) {
          // Upload optional revision attachments and pass URLs to the design_services update payload.
          const uploaded = await uploadFile(customerId, { file, folder: "proof-revisions" });
          const fileUrl = uploaded?.filePath || uploaded?.url || uploaded?.previewUrl;
          if (fileUrl) fileRefs.push(fileUrl);
        }
      }
      await handleBinatedRevision(managePoId, note, fileRefs);
      setBinatedRevisionModalOpen(false);
      setBinatedRevisionTargetFile(null);
      setBinatedRevisionNote("");
      setBinatedRevisionFiles([]);
      await fetchProofs();
    } finally {
      setBinatedRevisionSubmitting(false);
    }
  };
  const submitBinatedFromForm = async (poId) => {
    if (!poId) return;
    const payload = toProofingPayload(getProofingFormState(poId));
    const po = proofsByPo[String(poId)] || {};
    const started = hasBinatedStarted(po);
    try {
      setBinatedSubmitting(true);
      if (started && isBinatedUpdateAllowed(po)) {
        await handleBinatedUpdate(poId, payload);
      } else {
        await handleBinatedStart(poId, payload);
      }
      await fetchProofs();
      await loadPoFlags();
    } finally {
      setBinatedSubmitting(false);
    }
  };

  // 🔥 FAST PATH ON LOAD:
  // If rowsGate is true but the PO flag is not yet true/known, immediately flip UI and persist in background.
  useEffect(() => {
    if (!proofs?.length) return;
    if (!canManageProofs) return;
    const rowsGateByPo = computeRowsGateByPo(proofs); // { [poId]: boolean }

    Object.entries(rowsGateByPo).forEach(([pid, rowsGate]) => {
      if (!rowsGate) return; // up-only (never auto-uncheck)
      const alreadyTrue = !!poFlagsById[pid];
      const fired = !!didFastGateRef.current[pid];
      if (!alreadyTrue && !fired) {
        didFastGateRef.current[pid] = true;
        // 1) instant UI
        setPoFlagsById((prev) => ({ ...prev, [pid]: true }));
        // 2) persist in background (don’t block paint)
        setPoAllUploaded(Number(pid), true).catch((err) => {
          console.warn(
            "[ProofsList] fast gate-up failed for PO",
            pid,
            err?.message || err
          );
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proofs, canManageProofs]);

  // Group by PO — NO PHANTOM/DRAFT ROWS ANYMORE
  const proofsByPo = useMemo(() => {
    const byPo = {};

    proofs.forEach((p) => {
      if (!p?.id || String(p.id).toLowerCase() === "undefined") return;
      const pid = String(p.poId);
      if (!byPo[pid]) {
        byPo[pid] = {
          poId: Number(pid),
          poNumber: p.poNumber,
          purchaseOrderType: p.purchaseOrderType,
          productName: p.productName || "",
          productDescription: p.productDescription || "",
          supplierInfo: p.supplierInfo || null,
          // authoritative PO flag from lookupPurchaseOrders
          poAllProofsUploaded: !!poFlagsById[pid],
          decorations: Array.isArray(p.decorations) ? p.decorations : [],
          binatedMeta: p.poBinatedMeta || p.binatedMeta || null,
          proofsByLoc: {},
        };
      }

      // Keep proof rows visible even when filePath is null (soft-deleted / Proof Needed).
      // Upload/file-specific checks remain gated separately via hasUploadedFile().
      const decKey = `row-${p.id}`;
      byPo[pid].proofsByLoc[decKey] = p;
    });

    // Bundle gating uses ONLY rows that actually have uploaded files
    Object.entries(byPo).forEach(([pid, bucket]) => {
      const rows = Object.values(bucket.proofsByLoc);
      const eligible = rows.filter((r) => hasUploadedFile(r));
      const rowsGate =
        eligible.length > 0 &&
        eligible.every((r) => r.allProofsUploaded === true);

      // expose rowsGate for render decisions (e.g., hide Upload button)
      bucket.rowsGateReady = rowsGate;

      // show optimistic value immediately; overwrite once lookupPurchaseOrders resolves
      bucket.poAllProofsUploaded =
        pid in poFlagsById ? !!poFlagsById[pid] : rowsGate;

      const poGate = !!bucket.poAllProofsUploaded;
      bucket.bundleReady = rowsGate || poGate;

      // 🔎 Per-PO derivation log
      console.groupCollapsed(
        `[ProofsList] derive PO ${pid} (#${bucket.poNumber})`
      );
      console.log(
        j({
          from_poFlagsById: !!poFlagsById[pid],
          eligibleCount: eligible.length,
          eligibleRowIds: eligible.map((r) => r.id),
          eligibleRowReadyStates: eligible.map((r) => r.allProofsUploaded),
          rowsGate,
          poGate,
          bundleReady: bucket.bundleReady,
        })
      );
      console.groupEnd();
    });

    // expose for quick inspection
    if (typeof window !== "undefined") {
      window.__poFlagsById = poFlagsById;
      window.__proofsByPo = byPo;
    }

    return byPo;
  }, [proofs, poFlagsById]);

  const rowsForPo = (poId) =>
    Object.values(proofsByPo[String(poId)]?.proofsByLoc || {});

  // Manage-dialog-only rows: hide synthetic placeholder rows when PO has no saved proof entries
  // and no uploaded proof file yet.
  const manageRowsForPo = (poId) => {
    const rows = rowsForPo(poId);
    const po = proofsByPo[String(poId)] || {};

    return rows.filter((row) => {
      const rowId = String(row?.id || "");
      const poKey = String(po?.poId || poId || "");

      const hasFile = hasUploadedFile(row);
      const hasHistory = toAuditTrail(row).length > 0;

      // Backend fallback row produced from makeRow(null).
      // This row exists only so the PO appears in the list, but it is not
      // an actual saved proof row and should not render inside Manage.
      const isSyntheticEmptyPoRow =
        rowId === poKey &&
        !hasFile &&
        !hasHistory &&
        !row?.proofFilePath &&
        !row?.filePath;

      return !isSyntheticEmptyPoRow;
    });
  };

  useEffect(() => {
    if (!managePoId) return;
    setManageTab("proof_management");
    setManageBinatedTab("status");
    if (!canManageProofs) setManageSimpleMode(true);
    setBinatedFormByPo((prev) => {
      const key = String(managePoId);
      if (prev[key]) return prev;
      const po = proofsByPo[key] || {};
      const meta = getPoBinatedMeta(po);
      const initialReference = String(
        meta?.reference || getGeneratedPurchaseOrderUrl(po?.poId || managePoId) || ""
      ).trim();
      return {
        ...prev,
        [key]: {
          ...buildProofingFormState(managePoId),
          reference: initialReference,
        },
      };
    });
    setSelectedProofImportKeyByPo((prev) => {
      const key = String(managePoId);
      if (key in prev) return prev;
      return { ...prev, [key]: "" };
    });
  }, [managePoId, proofsByPo, canManageProofs]);

  useEffect(() => {
    if (!canManageProofs && manageSimpleMode !== true) {
      setManageSimpleMode(true);
    }
  }, [canManageProofs, manageSimpleMode]);

  useEffect(() => {
    if (!canManageProofs && manageTab !== "proof_management") {
      setManageTab("proof_management");
    }
  }, [canManageProofs, manageTab]);

  // --- Purchasing Popup parity helpers ---
  // 100% single source of truth for PO-level flag.
  const setPoAllUploaded = async (poId, val) => {
    if (blockProofMutation({ action: 'set-all-proofs-uploaded' })) return Boolean(poFlagsById[String(poId)]);
    try {
      const res = await updatePurchaseOrderDetails(poId, {
        allProofsUploaded: !!val,
      });
      const serverVal =
        res?.purchaseOrder?.allProofsUploaded ?? res?.allProofsUploaded ?? !!val;
      const serverProofStatus = normalizePurchaseOrderProofStatus(
        res?.purchaseOrder?.proofStatus
          ?? res?.po?.proofStatus
          ?? (serverVal ? "Proof Uploaded" : "Proof Needed")
      );

      // reflect authoritative server value immediately
      setPoFlagsById((prev) => ({ ...prev, [String(poId)]: !!serverVal }));
      setPoProofStatusById((prev) => ({ ...prev, [String(poId)]: serverProofStatus }));
      return !!serverVal;
    } catch (e) {
      console.warn("[ProofsList] setPoAllUploaded failed", e);
      throw e;
    }
  };

  const handlePoProofStatusChange = async (poId, nextStatus) => {
    if (blockProofMutation({ action: 'manage-proofs' })) return;
    const key = String(poId);
    const previousStatus = poProofStatusById[key] || "Proof Needed";
    if (previousStatus === nextStatus || poProofStatusUpdatingById[key]) return;

    setPoProofStatusById((prev) => ({ ...prev, [key]: nextStatus }));
    setPoProofStatusUpdatingById((prev) => ({ ...prev, [key]: true }));
    try {
      const result = await updatePurchaseOrderDetails(
        poId,
        buildPurchaseOrderProofStatusPatch(nextStatus)
      );
      const savedStatus = normalizePurchaseOrderProofStatus(
        result?.purchaseOrder?.proofStatus
          ?? result?.po?.proofStatus
          ?? result?.proofStatus
          ?? nextStatus
      );
      setPoProofStatusById((prev) => ({ ...prev, [key]: savedStatus }));
      invalidatePurchaseOrderCache(poId);
      publishPurchaseOrderInvalidation(poId);
      toast.success("Proof status updated");
    } catch (error) {
      setPoProofStatusById((prev) => ({ ...prev, [key]: previousStatus }));
      console.error("Failed to update PO proof status", error);
      toast.error(error?.response?.data?.message || "Failed to update Proof Status.");
    } finally {
      setPoProofStatusUpdatingById((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Compute the desired PO flag from current rows and persist via the PO route.
  const syncPoFlagWithRows = async (poId) => {
    const rows = rowsForPo(poId);
    const anyReady = rows.some((r) => !!r.allProofsUploaded);
    return setPoAllUploaded(poId, anyReady);
  };

  // Local optimistic patch helpers
  const patchProofLocal = (proofId, patch) => {
    setProofs((prev) =>
      prev.map((p) => (p.id === proofId ? normalizeRow({ ...p, ...patch }) : p))
    );
  };
  // Utility to call existing endpoint per-row (mirrors PurchasingPopup behaviour for row edits)
  const updateProofRow = (poId, proofId, patch = {}, opts = {}) => {
    if (blockProofMutation({ action: 'manage-proofs' })) return Promise.resolve(null);
    return approveProofList(poId, { proofId, ...patch, ...opts });
  };

  // Mutations (keep existing approveProofList for row-level/general field updates)
  const updateProofDetails = async (poId, payload) => {
    try {
      log("updateProofDetails payload:", j({ poId, payload }));
      const resp = await approveProofList(poId, payload);
      log("updateProofDetails resp:", j(resp));
      await fetchProofs();
      await loadPoFlags();
      return true;
    } catch (e) {
      console.error(e);
      toast.error("Failed to update proof");
      return false;
    }
  };

  const currentUserName = useMemo(() => {
    const firstName = String(currentUser?.first_name || currentUser?.firstName || "").trim();
    const lastName = String(currentUser?.last_name || currentUser?.lastName || "").trim();
    return [firstName, lastName].filter(Boolean).join(" ") || currentUser?.email || "Elite Core";
  }, [currentUser]);

  const currentUserId = currentUser?.id ?? currentUser?.userId ?? null;

  // Keep these overrides aligned with PurchasingPopup: approvals.user is the
  // internal/user decision and approvals.client is the client decision.
  const handleUserDecision = async (proof, isApproved) => {
    if (!canOverrideProofApproval) return;
    const ok = await updateProofDetails(proof.poId, {
      proofId: proof.id,
      source: "manual_pos_proofs_override",
      approvals: {
        user: {
          state: isApproved ? "approved" : "rejected",
          by: currentUserName,
          byId: currentUserId,
          at: new Date().toISOString(),
        },
      },
    });

    if (ok) {
      toast.success(
        `${currentUserName} ${isApproved ? "approved" : "rejected"} this proof`
      );
    }
  };

  const handleClientDecision = async (proof, isApproved) => {
    if (!canOverrideProofApproval) return;
    const ok = await updateProofDetails(proof.poId, {
      proofId: proof.id,
      source: "manual_pos_proofs_override",
      approvals: {
        client: {
          state: isApproved ? "approved" : "rejected",
          by: "Client",
          at: new Date().toISOString(),
        },
      },
    });

    if (ok) {
      toast.success(`Client ${isApproved ? "approved" : "rejected"} this proof`);
    }
  };

  const requestApprovalOverride = (proof, target, isApproved) => {
    setOverrideConfirm({ proof, target, isApproved });
  };

  const closeApprovalOverrideConfirm = () => {
    if (overrideConfirmBusy) return;
    setOverrideConfirm(null);
  };

  const confirmApprovalOverride = async () => {
    if (!overrideConfirm?.proof || overrideConfirmBusy) return;

    setOverrideConfirmBusy(true);
    try {
      if (overrideConfirm.target === "client") {
        await handleClientDecision(overrideConfirm.proof, overrideConfirm.isApproved);
      } else {
        await handleUserDecision(overrideConfirm.proof, overrideConfirm.isApproved);
      }
      setOverrideConfirm(null);
    } finally {
      setOverrideConfirmBusy(false);
    }
  };

  const handleStatusChange = async (proof, newStatus) => {
    if (blockProofMutation({ action: 'manage-proofs' })) return;
    const ok = await updateProofDetails(proof.poId, {
      proofId: proof.id,
      proofStatus: newStatus,
    });
    if (!ok) return;
    if (newStatus === "Rejected") toast("Proof marked as Rejected.");
    else if (newStatus === "Approved") toast.success("Proof marked as Approved.");
  };

  // Row-level "All proofs uploaded" — per-row update via approveProofList
  const handleProofsUploadedChange = async (proof, checked) => {
    if (blockProofMutation({ action: 'manage-proofs' })) return;
    log(
      "handleProofsUploadedChange",
      j({ proofId: proof.id, poId: proof.poId, checked })
    );

    if (!hasUploadedFile(proof)) {
      toast.warn("Upload a proof file first.");
      return;
    }

    const readinessPatch = buildProofReadinessPatch(proof, checked);

    // optimistic row patch
    patchProofLocal(proof.id, readinessPatch);

    try {
      await updateProofRow(
        proof.poId,
        proof.id,
        readinessPatch,
        {
          notifyOnReady: checked === true,
          source: "proofs_list",
        }
      );

      // ✅ single source of truth for PO flag (Purchasing Popup route)
      if (checked) {
        await syncPoFlagWithRows(proof.poId);
      } else {
        // Retracting readiness always clears this PO's aggregate flag. Other
        // POs are isolated by the id-scoped Purchasing Popup route.
        await setPoAllUploaded(proof.poId, false);
      }

      if (checked) {
        toast(
          "Client and team have been notified that this proof is ready."
        );
      } else {
        toast.success("Row marked as not ready.");
      }

      await fetchProofs();
      await loadPoFlags();
    } catch (e) {
      console.error(e);
      toast.error("Failed to update All proofs uploaded for this row.");
      await fetchProofs();
      await loadPoFlags();
    }
  };

  // PO-level "All proofs uploaded" — PO-level update via updatePurchaseOrderDetails (PurchasingPopup logic)
  const handlePoAllProofsUploaded = async (poId, checked) => {
    if (blockProofMutation({ action: 'set-all-proofs-uploaded' })) return;
    log("handlePoAllProofsUploaded input:", j({ poId, checked }));
    const poKey = String(poId);
    setPoFlagUpdatingById((prev) => ({ ...prev, [poKey]: true }));

    setPoFlagsById((prev) => ({ ...prev, [poKey]: checked }));
    setPoProofStatusById((prev) => ({
      ...prev,
      [poKey]: getPurchaseOrderProofStatusForReadiness(prev[poKey], checked),
    }));

    try {
      const val = await setPoAllUploaded(poId, checked);
      log("handlePoAllProofsUploaded result:", j({ poId, checked, val }));

      // patch local PO map immediately so UI reflects the change
      setPoFlagsById((prev) => ({ ...prev, [String(poId)]: !!val }));
      invalidatePurchaseOrderCache(poId);
      publishPurchaseOrderInvalidation(poId);

      toast.success(`All proofs ${val ? "marked uploaded" : "unmarked"}.`);
      await fetchProofs();
      await loadPoFlags();
    } catch (e) {
      console.error(e);
      toast.error("Failed to update PO-level All Proofs Uploaded.");
    } finally {
      setPoFlagUpdatingById((prev) => ({ ...prev, [poKey]: false }));
    }
  };

  // Multi-file upload
  const handleFilesUpload = async (files) => {
    if (blockProofMutation({ action: 'manage-proofs' })) return;
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));

    let decIdToSend = null;

    if (selectedProofId) {
      const row =
        proofs.find((p) => p.id === selectedProofId) ||
        rowsForPo(uploadPoId).find((p) => p.id === selectedProofId);

      if (row?.decorationId != null) decIdToSend = row.decorationId;
      formData.append("proofId", selectedProofId); // only for real rows
    }

    // Location is optional — only append when present (not null)
    if (decIdToSend != null) formData.append("decorationId", decIdToSend);

    try {
      const resp = await uploadProofList(uploadPoId, formData);
      log("uploadProofList resp:", resp);
      toast.success("Files uploaded.");
      setOpenModal(false);
      await fetchProofs();
      await loadPoFlags();
    } catch (e) {
      console.error(e);
      toast.error("Upload failed.");
    }
  };

  const handleManageDropFiles = async (poId, files = []) => {
    if (blockProofMutation({ action: 'manage-proofs' })) return;
    if (!poId || !Array.isArray(files) || !files.length) return;
    try {
      setManageUploadingAll(true);
      for (const file of files) {
        const formData = new FormData();
        formData.append("files", file);
        await uploadProofList(poId, formData);
      }
      toast.success(`${files.length} file(s) uploaded.`);
      await fetchProofs();
      await loadPoFlags();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Upload failed.");
    } finally {
      setManageUploadingAll(false);
    }
  };

  const handleManageRowDropFiles = async (proof, files = []) => {
    if (blockProofMutation({ action: 'manage-proofs' })) return;
    const targetPoId = proof?.poId ?? managePoId;
    const rowId = proof?.id ?? proof?.proofId ?? proof?.Id ?? proof?.proof_id ?? null;
    if (!targetPoId || !rowId || !Array.isArray(files) || !files.length) return;
    try {
      setManageUploadingAll(true);
      const file = files[0];
      const formData = new FormData();
      formData.append("files", file);
      formData.append("proofId", String(rowId));
      if (proof.decorationId != null) {
        formData.append("decorationId", String(proof.decorationId));
      }
      await uploadProofList(targetPoId, formData);
      toast.success("Proof uploaded.");
      await fetchProofs();
      await loadPoFlags();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Upload failed.");
    } finally {
      setManageUploadingAll(false);
    }
  };

  const handleManageProductionFiles = async (proof, files = []) => {
    if (blockProofMutation({ action: 'manage-proofs' })) return;
    const targetPoId = proof?.poId ?? managePoId;
    const rowId = proof?.id ?? proof?.proofId ?? proof?.Id ?? proof?.proof_id ?? null;
    if (!targetPoId || !rowId || !Array.isArray(files) || !files.length) return;

    try {
      setManageUploadingAll(true);
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      formData.append("proofId", String(rowId));
      formData.append("productionOnly", "true");
      if (proof.decorationId != null) {
        formData.append("decorationId", String(proof.decorationId));
      }
      await uploadProofList(targetPoId, formData);
      toast.success(`${files.length} production file${files.length === 1 ? "" : "s"} attached.`);
      await fetchProofs();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Production file upload failed.");
    } finally {
      setManageUploadingAll(false);
    }
  };

  const handleReuseApprovedProof = async (file) => {
    if (blockProofMutation({ action: 'manage-proofs' })) return;
    if (!reuseTarget) return;
    const approvals = reuseRequiresApproval
      ? {
          user: { state: "pending", by: null, at: null },
          client: { state: "pending", by: null, at: null },
        }
      : {
          user: { state: "approved", by: "Auto", at: new Date().toISOString() },
          client: { state: "approved", by: "Auto", at: new Date().toISOString() },
        };
    const proofStatus = reuseRequiresApproval ? "Uploaded" : "Approved";
    const ok = await updateProofDetails(reuseTarget.poId, {
      proofId: reuseTarget.id,
      filePath: file?.filePath,
      proofStatus,
      approvals,
      reuseApprovedProof: true,
      reuseApprovedBy: currentUser,
      reuseApprovedAt: new Date().toISOString(),
    });
    if (ok) {
      toast.success(
        reuseRequiresApproval
          ? "Approved proof attached for re-approval."
          : "Approved proof attached and auto-approved."
      );
    }
    setApprovedProofsOpen(false);
    setReuseTarget(null);
  };

  // Designer assignment (optional)
  const handleAssignDesigner = async (proof) => {
    if (blockProofMutation({ action: 'manage-proofs' })) return;
    try {
      const resp = await approveProofList(proof.poId, {
        proofId: proof.id,
        designerUserId: designerSelections[proof.id],
      });
      log("handleAssignDesigner resp:", j(resp));
    } catch (e) {
      toast.error("Failed to assign designer.");
      return;
    }
    toast.success("Designer assigned.");
    await fetchProofs();
    await loadPoFlags();
  };

  // Location reassignment (optional; supports null)
  const handleChangeLocation = async (proof, newDecId) => {
    if (blockProofMutation({ action: 'manage-proofs' })) return;
    const normalized =
      newDecId === "" || newDecId == null ? null : Number(newDecId);
    const ok = await updateProofDetails(proof.poId, {
      proofId: proof.id,
      decorationId: normalized, // null = Unassigned
    });
    if (ok) toast.success("Location updated for proof row.");
  };

  // Delete only uploaded file (preserve row/history)
  const handleDeleteUpload = async (proof) => {
    if (!proof?.id) return;
    if (blockProofMutation({ action: 'manage-proofs' })) return;
    const ok = await updateProofDetails(proof.poId, {
      proofId: proof.id,
      deleteUploadOnly: true,
      deletedBy: "Online Sales",
    });
    if (ok) toast.success("Upload deleted. Row reset to Proof Needed.");
  };

  // Delete row with confirmation
  const requestDeleteRow = (proof) => {
    if (blockProofMutation({ action: 'manage-proofs' })) return;
    setConfirmTarget(proof);
    setConfirmOpen(true);
  };

  const confirmDeleteRow = async () => {
    if (!confirmTarget) return;
    if (blockProofMutation({ action: 'manage-proofs' })) return;
    const ok = await updateProofDetails(confirmTarget.poId, {
      proofId: confirmTarget.id,
      deleteProofRow: true,
    });
    if (ok) toast.success("Proof row deleted.");
    setConfirmOpen(false);
    setConfirmTarget(null);
  };

  // Thumbnail/PDF renderer for Manage dialog
  const Thumb = ({ url, onDelete = null }) => {
    const ext = extFromUrl(url);
    const w = 100;
    const h = 100;

    if (!url) {
      return <Typography variant="caption">No Image</Typography>;
    }

    if (isImageExt(ext)) {
      return (
        <Box
          sx={{
            position: "relative",
            borderRadius: 1,
            overflow: "hidden",
            "&:hover .delBtn, &:focus-within .delBtn": {
              opacity: 1,
              transform: "scale(1)",
            },
          }}
        >
          <a href={url} target="_blank" rel="noopener noreferrer">
            <Box
              component="img"
              src={url}
              className="pm-thumb-img"
              sx={{ width: w, height: h, borderRadius: 1 }}
            />
          </a>
          {onDelete && (
            <IconButton
              aria-label="Delete image"
              title="Delete"
              className="delBtn"
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete();
              }}
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                opacity: 0,
                transform: "scale(0.9)",
                transition: "opacity 120ms ease, transform 120ms ease",
                bgcolor: "rgba(0,0,0,0.55)",
                color: "common.white",
                "&:hover": { bgcolor: "error.main" },
                zIndex: 2,
              }}
            >
              <DeleteForeverIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      );
    }

    if (isPdfExt(ext)) {
      return (
        <Box
          sx={{
            width: w,
            height: h,
            position: "relative",
            overflow: "hidden",
            "&:hover .delBtn, &:focus-within .delBtn": {
              opacity: 1,
              transform: "scale(1)",
            },
          }}
        >
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none" }}
          >
            <Box className="pdf-tile" sx={{ width: w, height: h }}>
              <object
                data={url}
                type="application/pdf"
                width={w}
                height={h}
                style={{ pointerEvents: "none" }}
              />
              <span>PDF</span>
            </Box>
          </a>
          {onDelete && (
            <IconButton
              aria-label="Delete pdf"
              title="Delete"
              className="delBtn"
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete();
              }}
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                opacity: 0,
                transform: "scale(0.9)",
                transition: "opacity 120ms ease, transform 120ms ease",
                bgcolor: "rgba(0,0,0,0.55)",
                color: "common.white",
                "&:hover": { bgcolor: "error.main" },
                zIndex: 2,
              }}
            >
              <DeleteForeverIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      );
    }

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: "none" }}
      >
        <Box className="file-tile" sx={{ width: w, height: h }}>
          FILE
        </Box>
      </a>
    );
  };

  // —————————————————————————————————————————————————————
  // Render
  // —————————————————————————————————————————————————————

  return (
    <Box className="proofs-section">
      {loading && <FloatingLoader />}
      <Toaster position="top-right" />

      <Box sx={{ mb: 1, px: 1 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Proof Provider:{" "}
          <b>
            {binatedSettings.enabled
              ? `${binatedSettings.provider || "binated"} (enabled)`
              : "disabled"}
          </b>
          {binatedSettings.enabled && !binatedSettings.ready
            ? " — missing credentials/base URL or provider mismatch."
            : ""}
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {/* PO rows */}
            {Object.entries(proofsByPo).map(([poId, po]) => {
              const rows = rowsForPo(poId);
              const counts = summarizeRows(rows);
              const statusChips = buildStatusChips(counts);

              // any row with a real file?
              const eligible = rows.filter((r) => hasUploadedFile(r));
              const hasAnyRealFile = eligible.length > 0;
              const reviewProof =
                eligible.find(
                  (r) => String(r?.proofStatus || "").toLowerCase() === "approved"
                ) || eligible[0] || null;

              // PurchasingPopup parity
              const poAllUploaded = !!po.poAllProofsUploaded; // ← reflect DB/mirror
              // ✅ must have at least one real upload to enable (or allow uncheck when already checked)
              const canMarkAllUploaded = poAllUploaded || hasAnyRealFile;
              const waitingForFlag =
                poFlagsLoading && !hasPoFlag(poFlagsById, poId);
              const updatingFlag = !!poFlagUpdatingById[String(poId)];
              const disableMarkAllUploadedWithoutProof =
                !canMarkAllUploaded && !poAllUploaded;
              const poNeedsProof = rows.some(
                (r) =>
                  String(r?.proofStatus || "").trim().toLowerCase() === "proof needed"
              );

              // 🔎 render-time snapshot per PO
              console.groupCollapsed(
                `[ProofsList] RENDER PO ${poId} (#${po.poNumber})`
              );
              console.log(
                j({
                  poAllUploaded_fromBucket: po.poAllProofsUploaded,
                  poFlagsById_forThis: poFlagsById[String(poId)],
                  eligibleCount: eligible.length,
                  eligibleRowIds: eligible.map((r) => r.id),
                  eligibleRowReady: eligible.map((r) => r.allProofsUploaded),
                })
              );
              console.groupEnd();

              const clientColor = aggToColor(counts.client);
              const userColor = aggToColor(counts.user);
              return (
                <TableRow key={poId} sx={{ "& .attach-approved-action": { opacity: 0 }, "&:hover .attach-approved-action, &:focus-within .attach-approved-action": { opacity: 1 } }}>
                  <TableCell
                    colSpan={2}
                    sx={{ py: 1.5, borderBottom: "1px solid #e1e1e1" }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ width: "100%" }}
                    >
                      {/* Left: PO number + quick peek chips + bundle link */}
                      <Box sx={{ ...oneLineWrap, gap: 8, minWidth: 0 }}>
                        <Typography
                          sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                        >
                          #{po.poNumber}
                        </Typography>
                        {poNeedsProof && (
                          <Chip
                            size="small"
                            label="Proof Needed"
                            color="warning"
                            sx={chipTight}
                          />
                        )}
                        <Box
                          sx={{ ...oneLineWrap, gap: 6, minWidth: 0, flex: 1 }}
                        >
                          {/* Status chips */}
                          <Box sx={{ ...oneLineWrap, gap: 6, minWidth: 0 }}>
                            {statusChips.map((c) => (
                              <Tooltip
                                key={c.key}
                                title={c.tooltip || c.label}
                                arrow
                                placement="top"
                              >
                                <Chip
                                  size="small"
                                  label={c.label}
                                  color={c.color}
                                  variant={c.variant || "filled"}
                                  sx={chipTight}
                                />
                              </Tooltip>
                            ))}
                          </Box>

                          {/* Approvals — shown after at least one real upload exists */}
                          {hasAnyRealFile && (
                            <>
                              <Tooltip
                                title={`Client • Approved: ${counts.client.approved} • Pending: ${counts.client.pending} • Rejected: ${counts.client.rejected}`}
                                arrow
                                placement="top"
                              >
                                <Chip
                                  size="small"
                                  label={`Client ${counts.client.approved}/${counts.client.pending}/${counts.client.rejected}`}
                                  color={clientColor}
                                  sx={chipTight}
                                />
                              </Tooltip>

                              <Tooltip
                                title={`User • Approved: ${counts.user.approved} • Pending: ${counts.user.pending} • Rejected: ${counts.user.rejected}`}
                                arrow
                                placement="top"
                              >
                                <Chip
                                  size="small"
                                  label={`User ${counts.user.approved}/${counts.user.pending}/${counts.user.rejected}`}
                                  color={userColor}
                                  sx={chipTight}
                                />
                              </Tooltip>
                            </>
                          )}

                          {/* Bundle ready */}
                          {/*po.bundleReady && (
                            <Tooltip
                              title="All eligible rows are uploaded and gated for bundle"
                              arrow
                              placement="top"
                            >
                              <Chip
                                size="small"
                                label="Bundle Ready"
                                color="success"
                                variant="outlined"
                                sx={chipTight}
                              />
                            </Tooltip>
                          )*/}

                          {/* Proof review (single proof link) */}
                          {po.bundleReady && reviewProof && (
                            <Button
                              size="small"
                              variant="text"
                              component="a"
                              href={getClientProofUrl(poId, reviewProof.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              Proof review
                            </Button>
                          )}
                        </Box>
                      </Box>

                      {/* Right actions */}
                      <Box sx={{ ...oneLineWrap, gap: 12 }}>
                        {canAttachApprovedPo && poNeedsProof && (
                          <Tooltip title="Match this PO with an already-approved sales-order proof">
                            <Button size="small" className="attach-approved-action" variant="text"
                              onClick={() => openAttachApproved(poId)}>
                              Attach Approved PO
                            </Button>
                          </Tooltip>
                        )}
                        <Tooltip
                          title={
                            !canManageProofs
                              ? PROOF_MANAGEMENT_READ_ONLY_MESSAGE
                              : disableMarkAllUploadedWithoutProof
                              ? "A proof must be uploaded before this can be checked."
                              : ""
                          }
                          arrow
                          placement="top"
                          disableHoverListener={!(!canManageProofs || disableMarkAllUploadedWithoutProof)}
                        >
                          <span>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={poAllUploaded}
                                  onChange={(e) =>
                                    handlePoAllProofsUploaded(
                                      poId,
                                      e.target.checked
                                    )
                                  }
                                  disabled={
                                    !canManageProofs ||
                                    waitingForFlag ||
                                    updatingFlag ||
                                    disableMarkAllUploadedWithoutProof
                                  } // read-only until loader completes
                                  sx={{
                                    "&.Mui-checked": {
                                      color: "rgb(199, 9, 9) !important",
                                    },
                                    "&.Mui-checked:hover": {
                                      backgroundColor:
                                        "rgba(199, 9, 9, 0.08) !important",
                                    },
                                    "&.Mui-focusVisible.Mui-checked": {
                                      outline: "2px solid rgba(199, 9, 9, 0.6)",
                                      outlineOffset: 2,
                                    },
                                    "&.Mui-disabled.Mui-checked": {
                                      color:
                                        "rgba(199, 9, 9, 0.38) !important",
                                    },
                                  }}
                                />
                              }
                              label={
                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                                  <span>All Proofs Uploaded</span>
                                  {(waitingForFlag || updatingFlag) && <CircularProgress size={14} />}
                                </Box>
                              }
                              sx={{ m: 0 }}
                            />
                          </span>
                        </Tooltip>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setManagePoId(poId)}
                        >
                          {canAccessProofManager ? "Manage" : "View"}
                        </Button>
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Empty state */}
            {!loading && Object.keys(proofsByPo).length === 0 && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography>No POs found.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* File Uploader Modal (multi-file) */}
      <CustomFileUploader
        open={openModal}
        onClose={() => setOpenModal(false)}
        onFilesUpload={canManageProofs ? handleFilesUpload : () => {}}
      />

      <Dialog open={Boolean(attachPoId)} onClose={() => setAttachPoId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Attach previously approved artwork — PO #{attachPoId}</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>
            We will match previously approved sales-order proofs to this purchase order.
            Select an approval to attach without asking the customer to approve again.
          </Typography>
          {attachLoading ? <CircularProgress size={24} /> : attachCandidates.length === 0 ? (
            <Box>
              <Typography color="text.secondary">No matching approved sales-order proof was found.</Typography>
              {attachDiagnostics && <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                {attachDiagnostics.matchingSalesOrderMappings === 0
                  ? "No active artwork approval mapping was found for the linked Sales Order. Check that the approved proof is saved on this Sales Order."
                  : attachDiagnostics.identifiedPoArtworkTargets === 0
                  ? "Approved mappings exist, but this PO has no identifiable artwork targets to safely attach them to."
                  : "Saved Sales Order mappings were found, but none passed the existing approval and target matching requirements."}
                {attachDiagnostics.reasons?.flatMap((entry) => entry.warnings || []).slice(0, 3).map((warning, index) => (
                  <Box key={index} sx={{ mt: 0.5 }}>{warning.message}</Box>
                ))}
              </Typography>}
            </Box>
          ) : attachCandidates.map((candidate) => (
            <Box key={candidate.mappingId} sx={{ py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                {candidate.filePath && /\\.(png|jpe?g|gif|webp)(?:[?#]|$)/i.test(candidate.filePath) && (
                  <Box component="a" href={candidate.filePath} target="_blank" rel="noopener noreferrer"
                    aria-label={`View ${candidate.fileName}`}>
                    <Box component="img" src={candidate.filePath} alt={candidate.fileName}
                      sx={{ width: 100, height: 90, objectFit: "contain", border: "1px solid", borderColor: "divider", borderRadius: 1 }} />
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600} sx={{ overflowWrap: "anywhere" }}>{candidate.fileName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {candidate.proofScope === "order" ? "Entire Order" : "Targeted proof"} · {candidate.coveredOrderItemIds?.length || 0} matched product(s) · Approved
                  </Typography>
                  {candidate.filePath && (
                    <Box><Button component="a" href={candidate.filePath} target="_blank" rel="noopener noreferrer"
                      size="small" sx={{ pl: 0 }}>View original proof ↗</Button></Box>
                  )}
                </Box>
                {candidate.alreadyAttached ? (
                  <Button size="small" color="warning" variant="outlined" disabled={attachSaving}
                    onClick={() => detachApproved(candidate.mappingId)}>
                    Remove inherited
                  </Button>
                ) : (
                  <Button size="small" variant="outlined" disabled={attachSaving}
                    onClick={() => attachApproved(candidate.mappingId)}>
                    Attach
                  </Button>
                )}
              </Box>
              <Box sx={{ mt: 1.5, px: 1.5, py: 1, bgcolor: "action.hover", borderRadius: 1 }}>
                <Typography variant="caption" fontWeight={700}>This approved artwork applies to:</Typography>
                {(candidate.matchedTargets || []).map((target, index) => (
                  <Typography key={index} variant="body2">
                    {[target.productName || (target.sku ? `Product ${target.sku}` : "Product"),
                      target.sku && target.productName ? `SKU ${target.sku}` : null,
                      target.method, target.location, target.artworkName]
                      .filter(Boolean).join(" · ")}
                  </Typography>
                ))}
                {(!candidate.matchedTargets || candidate.matchedTargets.length === 0) && (
                  <Typography variant="body2" color="text.secondary">
                    {candidate.coveredOrderItemIds?.length || 0} matched product(s); detailed decoration information unavailable.
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions><Button onClick={() => setAttachPoId(null)}>Close</Button></DialogActions>
      </Dialog>
      {/* Manage dialog */}
      <Dialog
        open={!!managePoId}
        onClose={() => setManagePoId(null)}
        maxWidth="md"
        fullWidth
        className="create-order-dialog"
      >
        <DialogTitle className="create-order-dialog-title">
          {canAccessProofManager ? "Manage proofs for PO #" : "View proofs for PO #"}
          {managePoId && proofsByPo[managePoId]?.poNumber}
        </DialogTitle>

        <DialogContent className="create-order-dialog-content">
          <Tabs
            value={manageTab}
            onChange={(_, val) => setManageTab(val)}
            sx={{ mb: 2 }}
          >
            <Tab value="proof_management" label="Proof Management" />
            {canManageProofs ? <Tab value="binated" label="Binated" /> : null}
          </Tabs>

          {managePoId && manageTab === "proof_management" && (
            <Box sx={{ mb: 2 }}>
              <FormControl
                size="small"
                sx={{ width: 280, mb: 2 }}
                disabled={!canManageProofs || !!poProofStatusUpdatingById[String(managePoId)]}
              >
                <InputLabel id="manage-po-proof-status-label">Proof Status</InputLabel>
                <Select
                  labelId="manage-po-proof-status-label"
                  label="Proof Status"
                  value={poProofStatusById[String(managePoId)] || "Proof Needed"}
                  onChange={(event) => handlePoProofStatusChange(managePoId, event.target.value)}
                >
                  {PURCHASE_ORDER_PROOF_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
              {canManageProofs && !(String(managePoId) in poFlagsById ? poFlagsById[String(managePoId)] : proofsByPo[managePoId]?.poAllProofsUploaded) ? (
                <DropArea
                  label="Drop proofs for this PO"
                  sublabel="Each file becomes its own proof row"
                  multiple
                  busy={manageUploadingAll}
                  onFiles={(files) => handleManageDropFiles(managePoId, files)}
                />
              ) : !canManageProofs ? (
                <Typography variant="body2" color="text.secondary">
                  Proof uploads are managed by Purchasing/Production.
                </Typography>
              ) : null}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Tooltip
                  title={
                    (() => {
                      if (!canManageProofs) {
                        return PROOF_MANAGEMENT_READ_ONLY_MESSAGE;
                      }
                      const manageRows = manageRowsForPo(managePoId);
                      const managePoAllUploaded = !!(
                        String(managePoId) in poFlagsById
                          ? poFlagsById[String(managePoId)]
                          : proofsByPo[managePoId]?.poAllProofsUploaded
                      );
                      const manageHasAnyRealFile = manageRows.some((r) => hasUploadedFile(r));
                      const hasMissingFiles = manageRows.some((r) => !hasUploadedFile(r));
                      if (hasMissingFiles) {
                        return "All rows must have an uploaded file before this can be checked.";
                      }
                      return !managePoAllUploaded && !manageHasAnyRealFile
                        ? "A proof must be uploaded before this can be checked."
                        : "";
                    })()
                  }
                  arrow
                  placement="top"
                  disableHoverListener={
                    (() => {
                      if (!canManageProofs) return false;
                      const manageRows = manageRowsForPo(managePoId);
                      const managePoAllUploaded = !!(
                        String(managePoId) in poFlagsById
                          ? poFlagsById[String(managePoId)]
                          : proofsByPo[managePoId]?.poAllProofsUploaded
                      );
                      const manageHasAnyRealFile = manageRows.some((r) => hasUploadedFile(r));
                      const hasMissingFiles = manageRows.some((r) => !hasUploadedFile(r));
                      return !hasMissingFiles && (managePoAllUploaded || manageHasAnyRealFile);
                    })()
                  }
                >
                  <span>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!(String(managePoId) in poFlagsById ? poFlagsById[String(managePoId)] : proofsByPo[managePoId]?.poAllProofsUploaded)}
                          onChange={(e) => handlePoAllProofsUploaded(managePoId, e.target.checked)}
                          disabled={
                            !canManageProofs ||
                            (!!poFlagsLoading && !hasPoFlag(poFlagsById, managePoId)) ||
                            !!poFlagUpdatingById[String(managePoId)] ||
                            manageRowsForPo(managePoId).some((r) => !hasUploadedFile(r)) ||
                            (!(
                              String(managePoId) in poFlagsById
                                ? poFlagsById[String(managePoId)]
                                : proofsByPo[managePoId]?.poAllProofsUploaded
                            ) &&
                              !manageRowsForPo(managePoId).some((r) => hasUploadedFile(r)))
                          }
                        />
                      }
                      label={
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                          <span>All Proofs Uploaded</span>
                          {((!!poFlagsLoading && !hasPoFlag(poFlagsById, managePoId)) || !!poFlagUpdatingById[String(managePoId)]) && (
                            <CircularProgress size={14} />
                          )}
                        </Box>
                      }
                      sx={{ m: 0 }}
                    />
                  </span>
                </Tooltip>
                {canManageProofs ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={manageSimpleMode}
                        onChange={(e) => setManageSimpleMode(e.target.checked)}
                      />
                    }
                    label="Simple view"
                    sx={{ m: 0 }}
                  />
                ) : null}
              </Box>
              </Box>
            </Box>
          )}

          {managePoId &&
            manageTab === "proof_management" &&
            manageRowsForPo(managePoId).map((proof) => {
              const po = proofsByPo[managePoId];

              // Location options: include Unassigned (default) + actual locations
              const locOptions = [
                { id: "", name: "Unassigned (default)" },
                ...(po?.decorations || []).map((d) => ({
                  id: String(d.decorationId),
                  name: d.Location || `Location ${d.decorationId}`,
                })),
              ];
              const currentLocId =
                proof.decorationId != null ? String(proof.decorationId) : "";

              const userState = (
                proof.approvals?.user?.state || "pending"
              ).toLowerCase();
              const clientState = (
                proof.approvals?.client?.state || "pending"
              ).toLowerCase();
              const proofStatus = proof.proofStatus || "Proof Needed";
              const hasFile = hasUploadedFile(proof);
              const thumbUrl = proof.proofFilePath || proof.filePath || null;
              const hideRowUpload = !!(
                String(managePoId) in poFlagsById
                  ? poFlagsById[String(managePoId)]
                  : proofsByPo[managePoId]?.poAllProofsUploaded
              );

              if (effectiveManageSimpleMode) {
                return (
                  <Box key={proof.id} sx={{ border: "1px solid #e6e6e6", borderRadius: 1, p: 1.5, mb: 1.5 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                      <Box sx={{ flex: "0 0 auto" }}>
                        {hasFile ? (
                          <Thumb
                            url={thumbUrl}
                            onDelete={canManageProofs && !hideRowUpload ? () => handleDeleteUpload(proof) : null}
                          />
                        ) : canManageProofs && !hideRowUpload ? (
                          <DropArea
                            label="Drop proof file"
                            sublabel="It will attach to this proof row"
                            multiple={false}
                            busy={manageUploadingAll}
                            onFiles={(files) => handleManageRowDropFiles(proof, files)}
                          />
                        ) : (
                          <Typography variant="caption">
                            {canManageProofs ? "No Image" : "Proof uploads are managed by Purchasing/Production."}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ minWidth: 220, flex: 1 }}>
                        <Typography sx={{ wordBreak: "break-all", fontWeight: 600 }}>
                          {proof.fileName || (thumbUrl ? thumbUrl.split("/").pop() : "—")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {proof.updatedAt
                            ? new Date(proof.updatedAt).toLocaleDateString()
                            : proof.createdAt
                            ? new Date(proof.createdAt).toLocaleDateString()
                            : ""}
                        </Typography>
                        {hasFile && (
                          <Button
                            size="small"
                            variant="text"
                            href={thumbUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
                            sx={{ mt: 0.5 }}
                          >
                            Download
                          </Button>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ mt: 1.5, width: "100%" }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Proof file status</InputLabel>
                        <Select
                          label="Proof file status"
                          value={proof.proofStatus || "Proof Needed"}
                          onChange={(e) => handleStatusChange(proof, e.target.value)}
                          disabled={!canManageProofs}
                        >
                          {PROOF_FILE_STATUS_OPTIONS.map((o) => (
                            <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid #ececec" }}>
                      <Typography variant="caption" sx={{ display: "block", fontWeight: 700, mb: 0.75 }}>
                        Files for Production
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        Internal source files. These are not shown to the customer and do not require approval.
                      </Typography>
                      {(Array.isArray(proof.productionFiles) ? proof.productionFiles : []).map((file, index) => {
                        const fileUrl = file?.filePath || file?.sourceFilePath || file?.downloadUrl || file?.url;
                        const fileName = file?.fileName || file?.sourceFileName || (fileUrl ? String(fileUrl).split("/").pop() : `Production file ${index + 1}`);
                        return fileUrl ? (
                          <Button
                            key={file?.sourceFileId || fileUrl || index}
                            size="small"
                            variant="text"
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<InsertDriveFileOutlinedIcon fontSize="small" />}
                            sx={{ mr: 1, mb: 0.5, textTransform: "none" }}
                          >
                            {fileName}
                          </Button>
                        ) : null;
                      })}
                      {canManageProofs ? (
                        <DropArea
                          label="Add production files"
                          sublabel="AI, EPS or any source file — not sent for customer approval"
                          multiple
                          busy={manageUploadingAll}
                          onFiles={(files) => handleManageProductionFiles(proof, files)}
                        />
                      ) : null}
                    </Box>

                    <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Chip size="small" label={`Internal: ${userState || "pending"}`} color={APPROVAL_COLOR[userState] || "default"} />
                      <Chip size="small" label={`Client: ${clientState || "pending"}`} color={APPROVAL_COLOR[clientState] || "default"} />
                      <Button size="small" variant="outlined" onClick={() => openProofHistory(proof)} sx={{ textTransform: "none" }}>
                        History ({toAuditTrail(proof).length})
                      </Button>
                    </Box>

                    {hasFile && canOverrideProofApproval && proofStatus === "Uploaded" && proof.id && (
                      <Box
                        sx={{
                          mt: 1,
                          pt: 1,
                          borderTop: "1px solid #ececec",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mr: 0.5, fontWeight: 600 }}
                        >
                          Client approval override
                        </Typography>
                        <Button
                          size="small"
                          disabled={clientState === "approved"}
                          onClick={() => requestApprovalOverride(proof, "client", true)}
                          sx={{ textTransform: "none" }}
                        >
                          Override client approve
                        </Button>
                        <Button
                          size="small"
                          disabled={clientState === "rejected"}
                          onClick={() => requestApprovalOverride(proof, "client", false)}
                          sx={{ textTransform: "none" }}
                        >
                          Override client reject
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              }

              return (
                <Box key={proof.id} className="proof-manage-row">
                  {/* Thumbnail / PDF tile */}
                  <div className="pm-thumb">
                    {hasFile ? (
                      <Thumb
                        url={thumbUrl}
                        onDelete={!hideRowUpload ? () => handleDeleteUpload(proof) : null}
                      />
                    ) : !hideRowUpload ? (
                      <DropArea
                        label="Drop proof file"
                        sublabel="It will attach to this proof row"
                        multiple={false}
                        busy={manageUploadingAll}
                        onFiles={(files) => handleManageRowDropFiles(proof, files)}
                      />
                    ) : (
                      <Typography variant="caption">No Image</Typography>
                    )}
                  </div>

                  {/* Details + actions */}
                  <div className="pm-main">
                    {/* Two-column field grid */}
                    <div className="pm-grid">
                      {/* Location */}
                      <div className="pm-field">
                        <span className="pm-label">Location (optional)</span>
                        <CustomDropdown
                          options={locOptions}
                          selectedValue={currentLocId}
                          handleSelect={(e) =>
                            handleChangeLocation(proof, e.target.value)
                          }
                          placeholder="Unassigned (default)"
                        />
                      </div>

                      {/* Status */}
                      <div className="pm-field">
                        <span className="pm-label">Row status</span>
                        <CustomDropdown
                          options={PROOF_FILE_STATUS_OPTIONS}
                          selectedValue={proof.proofStatus}
                          handleSelect={(e) =>
                            handleStatusChange(proof, e.target.value)
                          }
                        />
                      </div>
                    </div>

                    {/* Chips/Approvals */}
                    {hasFile && (
                      <div className="pm-chips">
                        <Tooltip
                          title={approvalTooltip(
                            "User",
                            proof.approvals?.user
                          )}
                          arrow
                          placement="top"
                        >
                          <Chip
                            size="small"
                            label={`User: ${userState}`}
                            color={APPROVAL_COLOR[userState] || "default"}
                            sx={chipTight}
                          />
                        </Tooltip>

                        <Tooltip
                          title={approvalTooltip(
                            "Client",
                            proof.approvals?.client
                          )}
                          arrow
                          placement="top"
                        >
                          <Chip
                            size="small"
                            label={`Client: ${clientState}`}
                            color={APPROVAL_COLOR[clientState] || "default"}
                            sx={chipTight}
                          />
                        </Tooltip>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openProofHistory(proof)}
                          sx={{ ...chipTight, textTransform: "none" }}
                        >
                          History ({toAuditTrail(proof).length})
                        </Button>

                        <Tooltip
                          title={
                            proofStatus === "Proof Needed"
                              ? "Revisions requested"
                              : `Proof status: ${getProofFileStatusLabel(proofStatus)}`
                          }
                          arrow
                          placement="top"
                        >
                          <Chip
                            size="small"
                            label={
                              proofStatus === "Proof Needed"
                                ? "Revisions requested"
                                : getProofFileStatusLabel(proofStatus)
                            }
                            color={
                              proofStatus === "Proof Needed"
                                ? "warning"
                                : STATUS_COLOR[proofStatus] || "default"
                            }
                            variant={
                              proofStatus === "Proof Needed"
                                ? "outlined"
                                : "filled"
                            }
                            sx={chipTight}
                          />
                        </Tooltip>

                        {proof?.reuseMeta?.reused && (
                          <Tooltip
                            title="This proof was reused from Approved Proofs"
                            arrow
                            placement="top"
                          >
                            <Chip
                              size="small"
                              label="Approved Proof (reused)"
                              color="info"
                              variant="outlined"
                              sx={chipTight}
                            />
                          </Tooltip>
                        )}
                      </div>
                    )}

                    {hasFile && canOverrideProofApproval && proofStatus === "Uploaded" && proof.id && (
                      <Box
                        sx={{
                          mt: 1,
                          pt: 1,
                          borderTop: "1px solid #ececec",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mr: 0.5, fontWeight: 600 }}
                        >
                          Approval overrides
                        </Typography>
                        <Button
                          size="small"
                          disabled={userState === "approved"}
                          onClick={() => requestApprovalOverride(proof, "user", true)}
                          sx={{ textTransform: "none" }}
                        >
                          Override user approval
                        </Button>
                        <Button
                          size="small"
                          disabled={userState === "rejected"}
                          onClick={() => requestApprovalOverride(proof, "user", false)}
                          sx={{ textTransform: "none" }}
                        >
                          Override user reject
                        </Button>
                        <Button
                          size="small"
                          disabled={clientState === "approved"}
                          onClick={() => requestApprovalOverride(proof, "client", true)}
                          sx={{ textTransform: "none" }}
                        >
                          Override client approve
                        </Button>
                        <Button
                          size="small"
                          disabled={clientState === "rejected"}
                          onClick={() => requestApprovalOverride(proof, "client", false)}
                          sx={{ textTransform: "none" }}
                        >
                          Override client reject
                        </Button>
                      </Box>
                    )}

                    {/* Client link appears once a file exists */}
                    {hasFile && (
                      <div className="pm-clientbar">
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Client proof link:&nbsp;
                          <a
                            href={getClientSingleProofUrl(managePoId, proof.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            (Open client review)
                          </a>
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          href={getClientSingleProofUrl(managePoId, proof.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open
                        </Button>
                        <Button
                          size="small"
                          onClick={async () => {
                            const url = getClientSingleProofUrl(
                              managePoId,
                              proof.id
                            );
                            try {
                              await navigator.clipboard.writeText(url);
                              toast.success("Client link copied");
                            } catch {
                              const ta = document.createElement("textarea");
                              ta.value = url;
                              document.body.appendChild(ta);
                              ta.select();
                              document.execCommand("copy");
                              document.body.removeChild(ta);
                              toast.success("Client link copied");
                            }
                          }}
                        >
                          Copy
                        </Button>
                      </div>
                    )}

                    <div className="pm-clientbar">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setReuseTarget(proof);
                          setApprovedProofsOpen(true);
                          setReuseRequiresApproval(true);
                          fetchApprovedProofs();
                        }}
                        disabled={!customerId}
                      >
                        Use Approved Proof
                      </Button>
                      {!customerId && (
                        <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                          Customer not resolved yet
                        </Typography>
                      )}
                    </div>

                    <Box sx={{ mt: 1.5, p: 1.5, border: "1px solid #e6e6e6", borderRadius: 1, bgcolor: "#fafafa" }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Files for Production
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        Internal source files attached to this proof. They never participate in customer approval.
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
                        {(Array.isArray(proof.productionFiles) ? proof.productionFiles : []).map((file, index) => {
                          const fileUrl = file?.filePath || file?.sourceFilePath || file?.downloadUrl || file?.url;
                          const fileName = file?.fileName || file?.sourceFileName || (fileUrl ? String(fileUrl).split("/").pop() : `Production file ${index + 1}`);
                          return fileUrl ? (
                            <Chip
                              key={file?.sourceFileId || fileUrl || index}
                              size="small"
                              icon={<InsertDriveFileOutlinedIcon />}
                              label={fileName}
                              component="a"
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              clickable
                              variant="outlined"
                            />
                          ) : null;
                        })}
                        {(!Array.isArray(proof.productionFiles) || proof.productionFiles.length === 0) && (
                          <Typography variant="caption" color="text.secondary">No production files attached yet.</Typography>
                        )}
                      </Box>
                      {canManageProofs ? (
                        <DropArea
                          label="Add production files"
                          sublabel="AI, EPS or any source file — not sent for customer approval"
                          multiple
                          busy={manageUploadingAll}
                          onFiles={(files) => handleManageProductionFiles(proof, files)}
                        />
                      ) : null}
                    </Box>

                    {/* Designer (optional) */}
                    <div className="pm-grid">
                      <div className="pm-field">
                        <span className="pm-label">Designer (optional)</span>
                        <CustomDropdown
                          options={designers}
                          selectedValue={designerSelections[proof.id] || ""}
                          handleSelect={(e) =>
                            setDesignerSelections((prev) => ({
                              ...prev,
                              [proof.id]: String(e.target.value),
                            }))
                          }
                          placeholder="Select designer"
                        />
                        <div style={{ marginTop: 6 }}>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => handleAssignDesigner(proof)}
                            disabled={!designerSelections[proof.id]}
                          >
                            Assign
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* File ops + Delete row */}
                  <div className="pm-actions">
                    {!hideRowUpload && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          // Always add new files as new proof rows from Manage dialog.
                          // Deleting old rows remains explicit via "Delete row".
                          setSelectedProofId(null);
                          setUploadPoId(managePoId);
                          setOpenModal(true);
                        }}
                      >
                        Add new files
                      </Button>
                    )}

                    <Button
                      size="small"
                      color="error"
                      onClick={() => requestDeleteRow(proof)}
                      disabled={hideRowUpload}
                    >
                      Delete row
                    </Button>
                  </div>

                  <Divider className="pm-divider" />
                </Box>
              );
            })}

          {managePoId && manageTab === "binated" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Tabs
                value={manageBinatedTab}
                onChange={(_, val) => setManageBinatedTab(val)}
              >
                <Tab value="status" label="Status" />
                <Tab value="form" label="Form" />
                <Tab value="payload" label="Payload Preview" />
              </Tabs>

              {manageBinatedTab === "status" && (() => {
                const po = proofsByPo[String(managePoId)] || {};
                const meta = getPoBinatedMeta(po);
                const normalizedStatus = String(
                  meta?.normalizedStatus || meta?.binatedProofStatus || meta?.binatedStatus || "submitted"
                ).toLowerCase().replace(/\s+/g, "_");
                const lifecycle = {
                  requestType: "design_service",
                  submittedAt: meta?.proofRequestedAt || meta?.createdAt || null,
                  updatedAt: meta?.proofLastSyncedAt || meta?.updatedAt || meta?.proofLastWebhookAt || null,
                  normalizedStatus,
                  aggregateStatus: meta?.aggregateStatus || null,
                  storedNormalizedStatus: meta?.storedNormalizedStatus || null,
                  statusItems: Array.isArray(meta?.statusItems) ? meta.statusItems : [],
                  revisionAllowed:
                    typeof meta?.revisionAllowed === "boolean"
                      ? meta.revisionAllowed
                      : normalizedStatus === "completed",
                };
                const files = Array.isArray(meta?.files) ? meta.files : [];
                const hasPlacementStatusItems = (
                  lifecycle.requestType === "design_service" &&
                  Array.isArray(lifecycle.statusItems) &&
                  lifecycle.statusItems.length > 0
                );
                return (
                  <BinatedLifecycleStatusPanel
                    lifecycle={lifecycle}
                    onRefresh={() => handleBinatedRefresh(managePoId)}
                    refreshing={binatedLifecycleRefreshing}
                    timeline={<BinatedLifecycleTimeline normalizedStatus={normalizedStatus} />}
                  >
                    {(normalizedStatus === "awaiting_review" || normalizedStatus === "ready" || files.length > 0) && (
                      <BinatedLifecycleFilesTable
                        files={files}
                        statusItems={lifecycle.statusItems}
                        revisionAllowed={!!lifecycle.revisionAllowed}
                        onRequestRevision={
                          hasPlacementStatusItems ? undefined : (file) => openBinatedRevisionModal(file)
                        }
                        disabled={binatedLifecycleRefreshing}
                      />
                    )}
                  </BinatedLifecycleStatusPanel>
                );
              })()}

              {manageBinatedTab === "form" && (() => {
                const form = getProofingFormState(managePoId);
                const po = proofsByPo[String(managePoId)] || {};
                const started = hasBinatedStarted(po);
                const canUpdate = isBinatedUpdateAllowed(po);
                const proofs = normalizeProofEntries(form?.proofs);
                const selectedProofIndex = getSelectedProofIndex(managePoId, proofs);
                const importOptions = getProofImportOptions(managePoId);
                const selectedImportKey = selectedProofImportKeyByPo[String(managePoId)] || "";
                const allProjectGeneratedPoUrls = Object.values(proofsByPo)
                  .map((candidatePo = {}) => getGeneratedPurchaseOrderUrl(candidatePo?.poId || candidatePo?.id))
                  .filter(Boolean);
                const primaryGeneratedPoUrl = getGeneratedPurchaseOrderUrl(po?.poId || po?.id || managePoId);
                const referencedGeneratedPoUrls = extractGeneratedPurchaseOrderLinks(form.reference);
                const generatedPoLinkOptions = [
                  primaryGeneratedPoUrl,
                  ...allProjectGeneratedPoUrls,
                  ...referencedGeneratedPoUrls,
                ].filter((value, idx, arr) => value && arr.indexOf(value) === idx);
                const activeGeneratedPoLinks = extractGeneratedPurchaseOrderLinks(form.reference);
                const handleReferenceChange = (nextValue = "") => {
                  updateProofingFormState(managePoId, {
                    reference: normalizeGeneratedPurchaseOrderReference(nextValue),
                  });
                };
                const toggleGeneratedPoReference = (url) => {
                  const activeSet = new Set(activeGeneratedPoLinks);
                  if (activeSet.has(url)) activeSet.delete(url);
                  else activeSet.add(url);
                  updateProofingFormState(managePoId, {
                    reference: [...activeSet].join(", "),
                  });
                };
                const currentProof = proofs[selectedProofIndex] || {
                  url: "",
                  proof_info: { product: "", garment: "", height: "100", width: "100", description: "" },
                };
                const isSmartSend = form.smartSend !== false;
                const hasBinatedReference = String(form.reference || "").trim().length > 0;
                const isBinatedSubmitting = !!binatedSubmitting;
                const hasRequiredManualBinatedFields =
                  !proofs.every((p) => !String(p?.url || "").trim()) &&
                  !(!form.approverEmail && started && !canUpdate);
                const canSubmitBinated =
                  !!binatedSettings.ready &&
                  !isBinatedSubmitting &&
                  hasBinatedReference &&
                  (isSmartSend || hasRequiredManualBinatedFields);
                return (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    <TextField label="Reference" size="small" value={form.reference} onChange={(e) => handleReferenceChange(e.target.value)} />
                    {generatedPoLinkOptions.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: -0.5 }}>
                        {generatedPoLinkOptions.map((url) => {
                          const isActive = activeGeneratedPoLinks.includes(url);
                          return (
                            <Button
                              key={url}
                              variant={isActive ? "contained" : "outlined"}
                              size="small"
                              onClick={() => toggleGeneratedPoReference(url)}
                            >
                              {url.split("/").pop()}
                            </Button>
                          );
                        })}
                      </Box>
                    )}
                    <TextField label="Notes" size="small" multiline minRows={2} value={form.notes} onChange={(e) => updateProofingFormState(managePoId, { notes: e.target.value })} />
                    <FormControlLabel
                      control={<Checkbox checked={!!form.cobranding} onChange={(e) => updateProofingFormState(managePoId, { cobranding: e.target.checked })} />}
                      label="Cobranding"
                    />
                    <FormControlLabel
                      control={<Checkbox checked={form.smartSend !== false} onChange={(e) => updateProofingFormState(managePoId, { smartSend: e.target.checked })} />}
                      label="Smart send"
                    />
                    <TextField label="Company Logo URL" size="small" value={form.company_logo_url} onChange={(e) => updateProofingFormState(managePoId, { company_logo_url: e.target.value })} />
                    {form.smartSend === false && (
                    <>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 1 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel id="proof-import-row-label">Import from PO Feed</InputLabel>
                        <Select
                          labelId="proof-import-row-label"
                          label="Import from PO Feed"
                          value={selectedImportKey}
                          onChange={(e) =>
                            setSelectedProofImportKeyByPo((prev) => ({
                              ...prev,
                              [String(managePoId)]: String(e.target.value),
                            }))
                          }
                        >
                          <MenuItem value="">Select PO row</MenuItem>
                          {importOptions.map((opt) => (
                            <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="outlined"
                        onClick={() => importProofRowToCurrent(managePoId, selectedImportKey)}
                        disabled={!selectedImportKey}
                      >
                        Import row
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => importAllProofRows(managePoId)}
                        disabled={importOptions.length === 0}
                      >
                        Import all
                      </Button>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                      <FormControl size="small" sx={{ minWidth: 170 }}>
                        <InputLabel id="proof-item-index-label">Proof Item</InputLabel>
                        <Select
                          labelId="proof-item-index-label"
                          label="Proof Item"
                          value={String(selectedProofIndex)}
                          onChange={(e) => setSelectedProofIndex(managePoId, Number(e.target.value))}
                        >
                          {proofs.map((_, idx) => (
                            <MenuItem key={`proof-${idx}`} value={String(idx)}>
                              Proof #{idx + 1}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button variant="outlined" onClick={() => addProofEntry(managePoId)}>
                        Add Proof
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => removeProofEntry(managePoId)}
                        disabled={proofs.length <= 1}
                      >
                        Remove Proof
                      </Button>
                    </Box>
                    <TextField
                      label="Proof URL"
                      size="small"
                      value={currentProof?.url || ""}
                      onChange={(e) => updateProofEntry(managePoId, selectedProofIndex, { url: e.target.value })}
                    />
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                      <TextField
                        label="Product"
                        size="small"
                        value={currentProof?.proof_info?.product || ""}
                        onChange={(e) =>
                          updateProofEntry(managePoId, selectedProofIndex, {
                            proof_info: { product: e.target.value },
                          })
                        }
                      />
                      <TextField
                        label="Garment"
                        size="small"
                        value={currentProof?.proof_info?.garment || ""}
                        onChange={(e) =>
                          updateProofEntry(managePoId, selectedProofIndex, {
                            proof_info: { garment: e.target.value },
                          })
                        }
                      />
                      <TextField
                        label="Height"
                        size="small"
                        value={currentProof?.proof_info?.height || ""}
                        onChange={(e) =>
                          updateProofEntry(managePoId, selectedProofIndex, {
                            proof_info: { height: e.target.value },
                          })
                        }
                      />
                      <TextField
                        label="Width"
                        size="small"
                        value={currentProof?.proof_info?.width || ""}
                        onChange={(e) =>
                          updateProofEntry(managePoId, selectedProofIndex, {
                            proof_info: { width: e.target.value },
                          })
                        }
                      />
                    </Box>
                    <TextField
                      label="Description"
                      size="small"
                      multiline
                      minRows={2}
                      value={currentProof?.proof_info?.description || ""}
                      onChange={(e) =>
                        updateProofEntry(managePoId, selectedProofIndex, {
                          proof_info: { description: e.target.value },
                        })
                      }
                    />
                    <Divider />
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                      <TextField label="Approver Name" size="small" value={form.approverName} onChange={(e) => updateProofingFormState(managePoId, { approverName: e.target.value })} />
                      <TextField label="Approver Email" size="small" value={form.approverEmail} onChange={(e) => updateProofingFormState(managePoId, { approverEmail: e.target.value })} />
                      <TextField label="Permission" size="small" value={form.approverPermission} onChange={(e) => updateProofingFormState(managePoId, { approverPermission: e.target.value })} />
                      <TextField label="Reminder Frequency" size="small" value={form.reminderFrequency} onChange={(e) => updateProofingFormState(managePoId, { reminderFrequency: e.target.value })} />
                      <TextField label="Reminder Interval" size="small" value={form.reminderInterval} onChange={(e) => updateProofingFormState(managePoId, { reminderInterval: e.target.value })} />
                      <TextField label="Reminder Weekdays (csv)" size="small" value={form.reminderWeekdays} onChange={(e) => updateProofingFormState(managePoId, { reminderWeekdays: e.target.value })} />
                      <TextField label="Reminder Timezone" size="small" value={form.reminderTimezone} onChange={(e) => updateProofingFormState(managePoId, { reminderTimezone: e.target.value })} />
                      <TextField label="Reminder Time" size="small" value={form.reminderTime} onChange={(e) => updateProofingFormState(managePoId, { reminderTime: e.target.value })} />
                    </Box>
                    <TextField label="Reminder End Date" size="small" value={form.reminderEndDate} onChange={(e) => updateProofingFormState(managePoId, { reminderEndDate: e.target.value })} />
                    </>
                    )}
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
                      <Button
                        variant="contained"
                        disabled={!canSubmitBinated}
                        onClick={() => submitBinatedFromForm(managePoId)}
                      >
                        {binatedSubmitting ? "Saving..." : started ? "Update Proof Request" : "Start Official Proof"}
                      </Button>
                    </Box>
                  </Box>
                );
              })()}

              {manageBinatedTab === "payload" && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                  <TextField
                    label="Generated Payload"
                    multiline
                    minRows={14}
                    fullWidth
                    value={JSON.stringify(toProofingPayload(getProofingFormState(managePoId)), null, 2)}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="PO Feed Example (for field mapping)"
                    multiline
                    minRows={14}
                    fullWidth
                    value={JSON.stringify(buildPoFeedPayloadExample(managePoId), null, 2)}
                    InputProps={{ readOnly: true }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

      <DialogActions>
          <Button onClick={() => setManagePoId(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <BinatedLifecycleRevisionModal
        open={binatedRevisionModalOpen}
        file={binatedRevisionTargetFile}
        note={binatedRevisionNote}
        onNoteChange={setBinatedRevisionNote}
        files={binatedRevisionFiles}
        onFilesAdd={(nextFiles = []) =>
          setBinatedRevisionFiles((prev) => [...prev, ...nextFiles].filter(Boolean))
        }
        onFileRemove={(idx) =>
          setBinatedRevisionFiles((prev) => prev.filter((_, i) => i !== idx))
        }
        onClose={closeBinatedRevisionModal}
        onSubmit={submitBinatedRevisionModal}
        submitting={binatedRevisionSubmitting}
      />

      {/* Approved proofs picker */}
      <Dialog
        open={approvedProofsOpen}
        onClose={() => {
          setApprovedProofsOpen(false);
          setReuseTarget(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select an approved proof</DialogTitle>
        <DialogContent dividers>
          <FormControlLabel
            control={
              <Checkbox
                checked={reuseRequiresApproval}
                onChange={(e) => setReuseRequiresApproval(e.target.checked)}
              />
            }
            label="Require re-approval (send for approval)"
            sx={{ mb: 1 }}
          />
          {approvedProofsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          ) : approvedProofs.length === 0 ? (
            <Typography variant="body2">No approved proofs found.</Typography>
          ) : (
            <List dense>
              {approvedProofs.map((file) => (
                <ListItem
                  key={file.id}
                  secondaryAction={
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleReuseApprovedProof(file)}
                    >
                      Attach
                    </Button>
                  }
                >
                  <ListItemText
                    primary={file.fileName || file.filePath}
                    secondary={file.folder}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApprovedProofsOpen(false);
              setReuseTarget(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual /pos-proofs approval override confirmation only. */}
      <Dialog
        open={!!overrideConfirm}
        onClose={closeApprovalOverrideConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Confirm approval override</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography variant="body2">
            Are you sure you want to override the{" "}
            <strong>{overrideConfirm?.target === "client" ? "client" : "internal/user"}</strong>{" "}
            approval and mark it as{" "}
            <strong>{overrideConfirm?.isApproved ? "approved" : "rejected"}</strong>?
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            This updates the saved proof approval immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size="small"
            onClick={closeApprovalOverrideConfirm}
            disabled={overrideConfirmBusy}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color={overrideConfirm?.isApproved ? "primary" : "error"}
            onClick={confirmApprovalOverride}
            disabled={overrideConfirmBusy}
          >
            {overrideConfirmBusy ? "Saving..." : "Yes, override"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete row confirmation */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Proof History</DialogTitle>
        <DialogContent dividers>
          {(() => {
            const trail = [...toAuditTrail(historyProof)].sort(
              (a, b) => new Date(b?.at || 0) - new Date(a?.at || 0)
            );
            if (!trail.length) {
              return <Typography variant="body2">No history yet.</Typography>;
            }
            return (
              <Box sx={{ display: "grid", gap: 1.25 }}>
                {trail.map((evt, idx) => {
                  const eventFile = evt?.filePath || historyProof?.proofFilePath || historyProof?.filePath || "";
                  const ext = extFromUrl(eventFile);
                  const atText = evt?.at ? new Date(evt.at).toLocaleString() : "—";
                  const actorName = evt?.actor?.name || "Unknown";
                  const actorEmail = evt?.actor?.email ? ` (${evt.actor.email})` : "";
                  return (
                    <Box
                      key={`${evt?.at || "evt"}-${idx}`}
                      sx={{ p: 1.25, border: "1px solid #eee", borderRadius: 1.5, display: "flex", gap: 1.25 }}
                    >
                      <Box
                        component={eventFile ? "a" : "div"}
                        href={eventFile || undefined}
                        target={eventFile ? "_blank" : undefined}
                        rel={eventFile ? "noopener noreferrer" : undefined}
                        sx={{
                          width: 48,
                          height: 48,
                          border: "1px solid #eee",
                          borderRadius: 1,
                          display: "grid",
                          placeItems: "center",
                          overflow: "hidden",
                          bgcolor: "#fafafa",
                          flexShrink: 0,
                        }}
                      >
                        {eventFile && isImageExt(ext) ? (
                          <img
                            src={eventFile}
                            alt="proof-history-preview"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : isPdfExt(ext) ? (
                          <PictureAsPdfOutlinedIcon sx={{ color: "#d32f2f" }} />
                        ) : (
                          <InsertDriveFileOutlinedIcon color="action" />
                        )}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2">
                          <b style={{ textTransform: "capitalize" }}>{evt?.type || "event"}</b> • {atText}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {actorName}
                          {actorEmail}
                        </Typography>
                        {(evt?.fromStatus || evt?.toStatus) && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {evt?.fromStatus || "—"} → {evt?.toStatus || "—"}
                          </Typography>
                        )}
                        {evt?.comment ? (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {evt.comment}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete row confirmation */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete this proof row?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently remove the proof row, including its history data,
            {confirmTarget?.proofFilePath ? " and its uploaded file(s)" : ""}.
            You can re-create a row later by uploading to this PO again or by
            assigning a location.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" onClick={confirmDeleteRow}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProofsList;
